/**
 * ImageGenerationPage - Mobile hub for creating images or posts
 * The UI mirrors the ImagePromptEditorPage layout for images and the PostEditPage media/description
 * patterns for manual posts, keeping state between tabs.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import '../MobilePostReview/ImagePromptEditorPage.css';
import '../MobilePostReview/PostEditPage.css';
import '../MobileAIStorage/ProjectDetailPage.css';
import './ImageGenerationPage.css';
import iconImage from '../../../assets/images/icons/icon-image.svg';
import iconUpload from '../../../assets/images/icons/icon-upload.svg';
import iconAi from '../../../assets/images/icons/icon-ai.svg';
import iconVideo from '../../../assets/images/icons/icon-video-generator.svg';
import iconDelete from '../../../assets/images/icons/icon-delete.svg';
import Skeleton from '../../effects/Skeleton';
import placeholderImage from '../../../assets/images/placeholder-post-image.png';
import ImageGalleryPage from '../MobileImageGallery/ImageGalleryPage';
import AIRegenerationModal from '../MobilePostReview/AIRegenerationModal.jsx';
import MobileCampaignSelector from '../MobileCampaignSelector/MobileCampaignSelector.jsx';
import useLanguage from '../../../hooks/useLanguage';
import useApi from '../../../hooks/useApi';
import useCampaign from '../../../hooks/useCampaign';
import useCompany from '../../../hooks/useCompany';
import {
  SUPPORTED_MEDIA_ACCEPT,
  SUPPORTED_MEDIA_LABEL,
  isSupportedMediaFile
} from '../mediaUploadConfig';
import { useMobileAlert } from '../common/alerts/MobileAlertContext';

const BackIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SaveIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 21H5C4.44772 21 4 20.5523 4 20V4C4 3.44772 4.44772 3 5 3H16L20 7V20C20 20.5523 19.5523 21 19 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M17 21V13H7V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M7 3V7H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CalendarIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="white" strokeWidth="2"/>
    <line x1="16" y1="2" x2="16" y2="6" stroke="white" strokeWidth="2"/>
    <line x1="8" y1="2" x2="8" y2="6" stroke="white" strokeWidth="2"/>
    <line x1="3" y1="10" x2="21" y2="10" stroke="white" strokeWidth="2"/>
  </svg>
);

const ClockIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
    <polyline points="12,6 12,12 16,14" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

const AIIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 18 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.56791 18.8123V17.9563H1.71194C1.23925 17.9563 0.856061 17.573 0.855972 17.1003C0.855972 16.6276 1.2392 16.2444 1.71194 16.2444H2.56791V15.3884C2.56791 14.9157 2.95119 14.5325 3.42389 14.5324C3.89666 14.5324 4.27986 14.9156 4.27986 15.3884V16.2444H5.13583C5.6086 16.2444 5.9918 16.6276 5.9918 17.1003C5.99171 17.573 5.60855 17.9563 5.13583 17.9563H4.27986V18.8123C4.27983 19.285 3.89664 19.6683 3.42389 19.6683C2.95121 19.6682 2.56794 19.285 2.56791 18.8123ZM10.4767 4.80819C10.6996 4.77883 10.927 4.79315 11.1455 4.85199H11.1446C11.5422 4.95852 11.8004 5.23185 11.9627 5.47207C12.1242 5.71114 12.2481 6.00066 12.3529 6.29619C12.5623 6.88697 12.7703 7.72584 13.0287 8.7586L13.1272 9.12687C13.1277 9.1282 13.1278 9.12959 13.1282 9.13085C13.1298 9.13146 13.1315 9.13219 13.1332 9.13284C13.2028 9.15873 13.3021 9.18814 13.4955 9.24332L14.7028 9.59467C15.0616 9.70418 15.3747 9.80788 15.6344 9.91118C15.8954 10.015 16.1552 10.1383 16.3729 10.2984C16.5953 10.4619 16.832 10.7075 16.9343 11.0667L16.9632 11.1822C17.0206 11.4541 17.0108 11.7369 16.9343 12.0053C16.832 12.3646 16.5953 12.6102 16.3729 12.7737C16.1552 12.9337 15.8954 13.0571 15.6344 13.1609C15.3747 13.2642 15.0616 13.3679 14.7028 13.4774L13.4955 13.8287C13.3017 13.884 13.2026 13.9134 13.1332 13.9392C13.1316 13.9398 13.1297 13.9396 13.1282 13.9402C13.1277 13.9417 13.1277 13.9436 13.1272 13.9452C13.1037 14.0159 13.0776 14.1158 13.0287 14.3125V14.3135C12.7703 15.3462 12.5623 16.1851 12.3529 16.7759C12.2482 17.0712 12.124 17.36 11.9627 17.599C11.8004 17.8393 11.5424 18.1135 11.1446 18.2201L11.1436 18.2191C10.8524 18.2974 10.5448 18.2978 10.2537 18.2191V18.2181C9.85746 18.1108 9.59965 17.839 9.43758 17.599C9.27623 17.36 9.15315 17.0703 9.04842 16.7749C8.83905 16.1842 8.62996 15.3461 8.3716 14.3135V14.3125C8.32287 14.1171 8.29666 14.0169 8.27306 13.9462C8.27238 13.9441 8.27171 13.9421 8.27107 13.9402C8.26992 13.9398 8.26831 13.9397 8.26709 13.9392C8.19746 13.9134 8.09824 13.8838 7.9048 13.8287H7.9038C7.01716 13.5751 6.28619 13.3673 5.76686 13.1609C5.50565 13.057 5.24523 12.9337 5.02734 12.7737C4.80497 12.6104 4.56776 12.3654 4.46499 12.0063V12.0053C4.37762 11.6987 4.37762 11.3734 4.46499 11.0667V11.0657L4.50978 10.9364C4.62696 10.6467 4.83277 10.4413 5.02734 10.2984C5.24523 10.1383 5.50565 10.015 5.76686 9.91118C6.28619 9.70471 7.01714 9.49701 7.9038 9.24332L8.26709 9.13284C8.26847 9.13233 8.26978 9.13134 8.27107 9.13085C8.27152 9.12954 8.2726 9.12826 8.27306 9.12687C8.29659 9.05618 8.32263 8.95638 8.3716 8.7596V8.7586L8.72792 7.36118C8.83852 6.94875 8.94375 6.5915 9.04842 6.29619C9.15315 6.00074 9.27615 5.71108 9.43758 5.47207C9.59972 5.23206 9.85767 4.95878 10.2547 4.85199L10.4767 4.80819Z" fill="currentColor"/>
  </svg>
);

const UploadIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="17 8 12 3 7 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="12" y1="3" x2="12" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const GalleryIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2"/>
    <path d="M3 15l4.5-3 3.5 2.5L18 11l3 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="8" cy="8.5" r="1.5" fill="currentColor"/>
  </svg>
);

const TrashIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const StrategyIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 5h14M5 12h9M5 19h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DualOptionToggle = ({
  active = false,
  onChange,
  leftLabel,
  rightLabel,
  disabled = false,
  ariaLabel = ''
}) => (
  <button
    type="button"
    className={`dual-option-toggle ${active ? 'checked' : 'unchecked'} ${disabled ? 'disabled' : ''}`}
    onClick={() => {
      if (disabled) return;
      onChange?.(!active);
    }}
    aria-pressed={active}
    aria-label={ariaLabel || (active ? rightLabel : leftLabel)}
    disabled={disabled}
  >
    <span className="dual-option-toggle-track" aria-hidden="true" />
    <span className="dual-option-toggle-label dual-option-toggle-off">{leftLabel}</span>
    <span className="dual-option-toggle-label dual-option-toggle-on">{rightLabel}</span>
  </button>
);

const TripleOptionToggle = ({
  value = 'post',
  onChange,
  labels = [],
  disabled = false,
  ariaLabel = '',
  className = ''
}) => {
  const options = labels.length === 3
    ? labels
    : [
        { value: 'post', label: 'Post' },
        { value: 'image', label: 'Immagini' },
        { value: 'video', label: 'Video' }
      ];
  const normalizedValue = options.some((option) => option.value === value) ? value : options[0].value;
  const activeIndex = Math.max(0, options.findIndex((option) => option.value === normalizedValue));
  const trackStyle = { transform: `translateX(${activeIndex * 100}%)` };

  return (
    <div
      className={`triple-option-toggle ${disabled ? 'disabled' : ''} ${className}`}
      role="tablist"
      aria-label={ariaLabel || 'Seleziona una tab'}
    >
      <span className="triple-option-toggle-track" aria-hidden="true" style={trackStyle} />
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={`triple-option-toggle-label ${normalizedValue === option.value ? 'active' : ''}`}
          onClick={() => {
            if (disabled) return;
            onChange?.(option.value);
          }}
          aria-pressed={normalizedValue === option.value}
          disabled={disabled}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
};

const AILoadingAnimation = ({ text = '' }) => (
  <div className="ai-loading-container">
    <div className="ai-loading-content">
      <div className="ai-loading-icon-container">
        <AIIcon size={24} />
        <div className="ai-loading-pulse"></div>
      </div>
      <div className="ai-loading-text">
        {text}
        <span className="ai-loading-dots">
          <span className="dot">.</span>
          <span className="dot">.</span>
          <span className="dot">.</span>
        </span>
      </div>
    </div>
    <div className="ai-loading-background"></div>
  </div>
);

const AILoadingOverlay = ({ text = '' }) => (
  <div className="ai-loading-container prompt-editor-ai-loading">
    <div className="ai-loading-dim" />
    <div className="ai-loading-content">
      <div className="ai-loading-icon-container">
        <AIIcon size={24} />
        <div className="ai-loading-pulse" />
      </div>
      <div className="ai-loading-text">
        {text}
        <span className="ai-loading-dots">
          <span className="dot">.</span>
          <span className="dot">.</span>
          <span className="dot">.</span>
        </span>
      </div>
    </div>
    <div className="ai-loading-background" />
  </div>
);

const DropdownSelect = ({
  value,
  options = [],
  onChange,
  placeholder = '',
  disabled = false,
  ariaLabel = '',
  className = '',
  isOpen,
  onOpenChange
}) => {
  const isControlled = typeof isOpen === 'boolean';
  const [openInternal, setOpenInternal] = useState(false);
  const open = isControlled ? isOpen : openInternal;
  const containerRef = useRef(null);
  const selectedOption = options.find((option) => `${option.value}` === `${value}`) || null;
  const displayLabel = selectedOption?.label || placeholder || options[0]?.label || '';

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        if (!isControlled) {
          setOpenInternal(false);
        }
        onOpenChange?.(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isControlled, onOpenChange, open]);

  return (
    <div className={`prompt-editor-dropdown ${open ? 'open' : ''} ${className}`} ref={containerRef}>
      <button
        type="button"
        className={`prompt-editor-dropdown-select ${open ? 'open' : ''}`}
        onClick={() => {
          if (disabled) return;
          const nextOpen = !open;
          if (!isControlled) {
            setOpenInternal(nextOpen);
          }
          onOpenChange?.(nextOpen);
        }}
        aria-label={ariaLabel || displayLabel}
        disabled={disabled}
      >
        <span>{displayLabel}</span>
        <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </button>
      {open && !disabled && (
        <div className="prompt-editor-dropdown-menu">
          {options.map((option) => {
            const isActive = `${option.value}` === `${value}`;
            return (
              <button
                key={`${option.value}-${option.label}`}
                className={`prompt-editor-dropdown-item ${isActive ? 'active' : ''}`}
                onClick={() => {
                  onChange?.(option.value);
                  if (!isControlled) {
                    setOpenInternal(false);
                  }
                  onOpenChange?.(false);
                }}
                type="button"
              >
                {option.label}
                {isActive && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M20 6L9 17L4 12"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const generateMediaId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `media-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const splitPromptIntoBlocks = (prompt) => {
  if (!prompt) return [];
  const byLines = prompt.split('\n').map((p) => p.trim()).filter(Boolean);
  if (byLines.length > 1) return byLines;
  const bySentence = prompt.split(/(?<=[.!?])\s+/).map((p) => p.trim()).filter(Boolean);
  if (bySentence.length > 1) return bySentence;
  return [prompt.trim()];
};

const PROMPT_KEY_ALIASES = {
  soggetto: 'subject',
  subject: 'subject',
  composizione: 'composition',
  composition: 'composition',
  stile: 'style',
  style: 'style',
  camera: 'camera',
  luce: 'lighting',
  lighting: 'lighting',
  illuminazione: 'lighting',
  tono: 'tone',
  tone: 'tone',
  qualita: 'quality',
  qualità: 'quality',
  quality: 'quality',
  colori: 'color_palette',
  palette: 'color_palette',
  'palette_colori': 'color_palette',
  mood: 'mood',
  atmosfera: 'mood',
  notes: 'additional_notes',
  note: 'additional_notes',
  'note_aggiuntive': 'additional_notes',
  'altri_dettagli': 'additional_notes',
  'additional notes': 'additional_notes',
  'additional_notes': 'additional_notes',
  'detagli': 'additional_notes',
  metadata: 'metadata',
  instructions: 'instructions'
};

const PROMPT_JSON_KEY_ORDER = [
  'subject',
  'composition',
  'style',
  'camera',
  'lighting',
  'mood',
  'tone',
  'quality',
  'color_palette',
  'additional_notes'
];

const RESERVED_PROMPT_JSON_KEYS = new Set(['metadata', 'reference_images', 'instructions']);

const PROMPT_LABEL_DEFAULTS = {
  en: {
    subject: 'Subject',
    composition: 'Composition',
    style: 'Style',
    camera: 'Camera',
    lighting: 'Lighting',
    mood: 'Mood',
    tone: 'Tone',
    quality: 'Quality',
    color_palette: 'Color palette',
    additional_notes: 'Additional notes'
  },
  it: {
    subject: 'Soggetto',
    composition: 'Composizione',
    style: 'Stile',
    camera: 'Camera',
    lighting: 'Illuminazione',
    mood: 'Atmosfera',
    tone: 'Tono',
    quality: 'Qualità',
    color_palette: 'Palette colori',
    additional_notes: 'Note aggiuntive'
  }
};

const BLOCK_PLACEHOLDER_EXAMPLES = {
  subject: 'Es: Un cane con le gambe di coccodrillo',
  composition: 'Es: Inquadratura dall’alto su un picnic un po’ pasticciato',
  style: 'Es: Stile fumetto anni 90, colori super saturi',
  camera: 'Es: Scatto fish-eye da skater che fa una piega',
  lighting: 'Es: Luci al neon da discoteca anni 80',
  mood: 'Es: Atmosfera festa sorpresa con amici che ridono',
  tone: 'Es: Tono ironico ma curato, zero formalità',
  quality: 'Es: Alta definizione ma niente posa da catalogo',
  color_palette: 'Es: Palette gelato pistacchio, fragola e limone',
  additional_notes: 'Es: Niente gatti volanti stavolta (forse)',
  default: 'Es: Aggiungi qui cosa vuoi vedere generato'
};

const PROMPT_MIN_LENGTH = 10;
const PROMPT_MAX_LENGTH = 1000;

const normalizeLabelToKey = (label, fallbackIndex = 0) => {
  if (!label) {
    return `section_${fallbackIndex + 1}`;
  }

  const normalized = label
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s_-]/g, '')
    .replace(/\s+/g, '_');

  if (!normalized) {
    return `section_${fallbackIndex + 1}`;
  }

  return PROMPT_KEY_ALIASES[normalized] || normalized;
};

const parsePromptBlock = (block) => {
  const trimmed = typeof block === 'string' ? block.trim() : '';
  if (!trimmed) {
    return { label: '', value: '' };
  }

  const match = /^\s*([^:]{1,80}):\s*(.*)$/.exec(trimmed);
  if (match) {
    return {
      label: match[1].trim(),
      value: match[2].trim()
    };
  }

  return {
    label: '',
    value: trimmed
  };
};

const buildPromptJsonFromBlocks = (blocks = [], options = {}) => {
  const {
    normalizedRatio,
    quality,
    tone,
    creativity,
    simplePrompt,
    referenceImages = [],
    mode,
    baseReferenceIncluded = false,
    includeReferenceStats = false
  } = options;

  const promptJson = {};

  blocks.forEach((block, index) => {
    const { label, value } = parsePromptBlock(block);
    if (!value) {
      return;
    }
    const key = normalizeLabelToKey(label || `section_${index + 1}`, index);
    if (!key || RESERVED_PROMPT_JSON_KEYS.has(key)) {
      return;
    }
    promptJson[key] = value;
  });

  const metadata = {};
  if (normalizedRatio) {
    metadata.aspect_ratio = normalizedRatio;
  }
  if (quality) {
    metadata.quality = quality;
  }
  if (tone) {
    metadata.tone = tone;
  }
  const normalizedCreativity = typeof creativity === 'number' ? Math.min(1, Math.max(0, parseFloat(creativity))) : undefined;
  if (normalizedCreativity !== undefined && !Number.isNaN(normalizedCreativity)) {
    metadata.creativity = normalizedCreativity;
  }
  if (mode) {
    metadata.mode = mode;
  }
  if (includeReferenceStats) {
    if (typeof baseReferenceIncluded === 'boolean') {
      metadata.base_reference_included = baseReferenceIncluded;
    }
    if (Array.isArray(referenceImages)) {
      metadata.reference_images_supplied = referenceImages.length;
    }
  }

  if (Object.keys(metadata).length > 0) {
    promptJson.metadata = metadata;
  }

  if (Array.isArray(referenceImages) && referenceImages.length > 0) {
    promptJson.reference_images = referenceImages;
  }

  if (simplePrompt) {
    promptJson.instructions = simplePrompt;
  }

  return promptJson;
};

const buildPromptJsonSchema = (promptJson = {}) => {
  const schema = {};
  Object.keys(promptJson).forEach((key) => {
    if (RESERVED_PROMPT_JSON_KEYS.has(key)) {
      return;
    }
    schema[key] = null;
  });
  return Object.keys(schema).length > 0 ? schema : null;
};

const buildBlocksFromPromptJson = (promptJson, resolveLabel) => {
  if (!promptJson || typeof promptJson !== 'object') {
    return [];
  }

  const blocks = [];
  const visited = new Set();

  const appendBlock = (key) => {
    if (!key || visited.has(key) || RESERVED_PROMPT_JSON_KEYS.has(key)) {
      return;
    }

    const value = promptJson[key];
    if (value === undefined || value === null || (typeof value === 'string' && value.trim() === '')) {
      return;
    }

    let content = value;
    if (typeof value === 'object') {
      try {
        content = JSON.stringify(value);
      } catch (error) {
        content = String(value);
      }
    }

    const label = resolveLabel ? resolveLabel(key) : key;
    blocks.push(`${label}: ${content}`);
    visited.add(key);
  };

  PROMPT_JSON_KEY_ORDER.forEach(appendBlock);

  Object.keys(promptJson).forEach((key) => {
    appendBlock(key);
  });

  return blocks;
};

const resolveDnaId = (dna) => {
  if (!dna) return null;
  return dna.id ?? dna._id ?? dna.dna_id ?? dna.value ?? dna.key ?? null;
};

const resolveMediaFileId = (media) => {
  if (!media) return null;
  const candidates = [
    media.mediaFileId,
    media.media_file_id,
    media.mediaId,
    media.media_id,
    media.file_id,
    media.fileId,
    media.raw?.mediaFileId,
    media.raw?.media_file_id,
    media.raw?.mediaId,
    media.raw?.media_id,
    media.raw?.id,
    media.raw?._id
  ];

  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null) continue;
    const value = `${candidate}`.trim();
    if (value) return value;
  }

  // If this media comes from a local file/blob, treat it as a fresh upload and
  // avoid using locally generated ids as media_file_ids.
  const hasLocalFile = media?.file instanceof File || media?.file instanceof Blob;
  if (hasLocalFile) {
    return null;
  }

  const directId = media.id ?? media._id;
  if (directId !== undefined && directId !== null) {
    const value = `${directId}`.trim();
    if (value && !value.startsWith('media-')) {
      return value;
    }
  }

  return null;
};

const toAbsoluteHttpUrl = (value) => {
  if (!value) return null;
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : undefined;
    const parsed = new URL(value, base);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
      return parsed.href;
    }
    return null;
  } catch (error) {
    console.warn('[ImageGenerationPage] Skipping invalid reference url', value, error);
    return null;
  }
};

const normalizeStrategyItems = (items = [], t) =>
  items
    .map((item) => ({
      id: item?.id ?? item?.value ?? item?.key,
      label: item?.name ?? item?.label ?? item?.title ?? t('imageGenerator.settings.strategyFallback', { defaultValue: 'Strategia' }),
      description: item?.description ?? item?.subtitle ?? ''
    }))
    .filter((item) => item.id !== undefined && item.id !== null);

const normalizeDnaItems = (items = [], t) =>
  items
    .map((item) => ({
      id: resolveDnaId(item),
      label: item?.name ?? item?.label ?? item?.title ?? t('imageGenerator.settings.dnaFallback', { defaultValue: 'DNA' }),
      description: item?.description ?? item?.subtitle ?? ''
    }))
    .filter((item) => item.id !== undefined && item.id !== null);

const resolveCustomRatio = (widthValue, heightValue, fallback = '4:5') => {
  const width = parseInt(widthValue, 10);
  const height = parseInt(heightValue, 10);
  if (!width || !height) return fallback;
  const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
  const divisor = gcd(width, height);
  return `${Math.round(width / divisor)}:${Math.round(height / divisor)}`;
};

const normalizeVideoCapabilityValue = (value) => `${value ?? ''}`.trim().toLowerCase();

const normalizeVideoFpsValue = (value) => {
  const parsed = parseInt(`${value ?? ''}`.replace(/[^\d]/g, ''), 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const getModelCapabilityList = (model, keys = []) => {
  if (!model || !keys.length) return null;
  const sources = [model.capabilities, model.metadata];
  for (const source of sources) {
    if (!source) continue;
    for (const key of keys) {
      const value = source?.[key];
      if (Array.isArray(value) && value.length) {
        return value;
      }
      if (value && Array.isArray(value.values) && value.values.length) {
        return value.values;
      }
      if (value && Array.isArray(value.options) && value.options.length) {
        return value.options;
      }
    }
  }
  return null;
};

const MAX_IMAGE_UPLOAD_SIZE_BYTES = 5 * 1024 * 1024;
const isImageFile = (file) => {
  if (!file) return false;
  const mimeType = (file.type || '').toLowerCase();
  if (mimeType.startsWith('image/')) return true;
  const fileName = (file.name || '').toLowerCase();
  return /\.(jpe?g|png|gif|webp)$/.test(fileName);
};
const isImageTooLarge = (file) => (
  isImageFile(file) && typeof file.size === 'number' && file.size > MAX_IMAGE_UPLOAD_SIZE_BYTES
);

const normalizeHashtags = (hashtags) => {
  if (!Array.isArray(hashtags)) return [];
  return hashtags
    .map((h) => `${h || ''}`.trim())
    .filter(Boolean)
    .map((h) => (h.startsWith('#') ? h : `#${h}`));
};

const mapRatioToSize = (ratio, customWidth, customHeight) => {
  if (ratio === 'custom') {
    const width = parseInt(customWidth, 10) || 1024;
    const height = parseInt(customHeight, 10) || 1024;
    return `${width}x${height}`;
  }
  const presets = {
    '1:1': '1024x1024',
    '4:5': '1024x1280',
    '5:4': '1280x1024',
    '16:9': '1280x720',
    '9:16': '720x1280',
    '4:3': '1200x900',
    '3:4': '900x1200',
    '21:9': '1600x685'
  };
  return presets[ratio] || '1024x1024';
};

const MAX_REFERENCE_IMAGES = 3;
const VIDEO_PROMPT_MIN_LENGTH = 1;
const VIDEO_PROMPT_MAX_LENGTH = 2000;
const VIDEO_RATIO_OPTIONS = ['1:1', '4:5', '5:4', '16:9', '9:16'];
const VIDEO_DEFAULT_RESOLUTION = '720p';
const VIDEO_RESOLUTION_OPTIONS = ['720p', '1080p', '2k', '4k'];
const VIDEO_FPS_OPTIONS = [
  { value: 24, label: '24fps' },
  { value: 30, label: '30fps' },
  { value: 60, label: '60fps' }
];
const VIDEO_DURATION_OPTIONS = Array.from({ length: 30 }, (_, idx) => idx + 1);
const VIDEO_COUNT_OPTIONS = [1, 2, 3, 4];

const mergeUniqueById = (...lists) => {
  const map = new Map();
  lists.flat().filter(Boolean).forEach((item) => {
    if (!item || item.id === undefined || item.id === null) return;
    const key = `${item.id}`;
    map.set(key, { ...map.get(key), ...item });
  });
  return Array.from(map.values());
};

const extractDnaFromCompany = (company, t) => {
  if (!company) return null;
  const dnaSource = company.generated_dna || company.dna || company.ai_analysis?.company_identity;
  if (!dnaSource) return null;
  const description = typeof dnaSource === 'string'
    ? dnaSource
    : dnaSource.dna || dnaSource.mission || dnaSource.summary || JSON.stringify(dnaSource);
  return {
    id: company.id || company._id,
    label: company.name || t('imageGenerator.settings.dnaFallback', { defaultValue: 'DNA' }),
    description
  };
};

const extractStrategiesFromCampaigns = (campaignList = [], t) => {
  return campaignList
    .filter((campaign) => campaign?.strategy)
    .map((campaign) => ({
      id: campaign.id || campaign._id || campaign.value,
      label: campaign.name || campaign.title || t('imageGenerator.settings.strategyFallback', { defaultValue: 'Strategia' }),
      description: campaign.strategy?.desc || campaign.strategy?.description || campaign.strategy?.summary || ''
    }));
};

const deriveStrategyIdFromCampaign = (campaign) => {
  if (!campaign || !campaign.strategy) return null;
  return campaign.strategy.id || campaign.strategy._id || campaign.strategy.value || campaign.id || campaign._id || campaign.value || null;
};

const extractPlatformsFromCampaign = (campaign) => {
  if (!campaign) return [];
  if (Array.isArray(campaign.target_platforms) && campaign.target_platforms.length) return campaign.target_platforms;
  if (Array.isArray(campaign.platforms) && campaign.platforms.length) return campaign.platforms;
  if (campaign.platform) return [campaign.platform];
  if (Array.isArray(campaign.socialChannels) && campaign.socialChannels.length) return campaign.socialChannels;
  return [];
};

const ImageGenerationPage = ({
  onBack,
  onGenerate,
  onGeneratePostAuto,
  onCreatePostManual,
  onGenerateDescription,
  onGenerateManualImageFromPrompt,
  isLoading = false,
  initialMode = 'simple',
  initialTab = 'post',
  defaultPrompt = '',
  initialSimplePrompt = '',
  availableStrategies = null,
  availableDnaProfiles = null,
  campaigns = null,
  initialStrategyId = null,
  initialDnaId = null,
  initialGeneratedImage = null,
  initialAspectRatio = '1:1'
}) => {
  const { t, language } = useLanguage();
  const { notify, confirm: mobileConfirm } = useMobileAlert();
  const apiClient = useApi();
  const {
    campaigns: fetchedCampaigns,
    loading: campaignsLoading
  } = useCampaign();
  const {
    currentCompany,
    companies: companyList,
    loading: companyLoading
  } = useCompany();

  const normalizedDefaultPrompt = useMemo(
    () => (defaultPrompt && defaultPrompt.trim()) || '',
    [defaultPrompt]
  );

  const promptTemplateLines = useMemo(() => {
    const value = t('imageGenerator.defaults.promptTemplate', { returnObjects: true });
    if (Array.isArray(value)) {
      return value.map((entry) => (typeof entry === 'string' ? entry : '')).filter(Boolean);
    }
    return [];
  }, [t, language]);

  const languageCode = useMemo(() => (
    typeof language === 'string' && language
      ? language.split('-')[0]?.toLowerCase() || 'en'
      : 'en'
  ), [language]);

  const labelDefaults = useMemo(() => (
    PROMPT_LABEL_DEFAULTS[languageCode] || PROMPT_LABEL_DEFAULTS.en
  ), [languageCode]);

  const unsupportedFormatMessage = useMemo(
    () => t(
      'postReview.postEdit.media.unsupportedFormat',
      { defaultValue: `Formato non supportato. Usa: ${SUPPORTED_MEDIA_LABEL}` }
    ),
    [t]
  );
  const imageTooLargeMessage = useMemo(
    () => t('postReview.postEdit.media.maxSize', { defaultValue: 'Le immagini possono pesare al massimo 5MB.' }),
    [t]
  );

  const buildEmptyBlocksFromTemplate = () => {
    if (!promptTemplateLines.length) {
      return [''];
    }

    return promptTemplateLines.map((line, lineIdx) => {
      const { label } = parsePromptBlock(line);
      const fallbackLabel = t('imageGenerator.pro.blockFallback', {
        index: lineIdx + 1,
        defaultValue: `Blocco ${lineIdx + 1}`
      });
      const safeLabel = label || fallbackLabel;
      return `${safeLabel}: `;
    });
  };

  const buildBlockPlaceholder = (label, idx) => {
    const normalizedKey = normalizeLabelToKey(label, idx);
    return BLOCK_PLACEHOLDER_EXAMPLES[normalizedKey] || BLOCK_PLACEHOLDER_EXAMPLES.default;
  };

  const [campaignOptions, setCampaignOptions] = useState(campaigns || []);
  const [strategyOptions, setStrategyOptions] = useState(availableStrategies || []);
  const [dnaOptions, setDnaOptions] = useState(availableDnaProfiles || []);
  const [bootstrapError, setBootstrapError] = useState('');
  const [activeTab, setActiveTab] = useState(() => {
    if (initialTab === 'image') return 'image';
    if (initialTab === 'video') return 'video';
    return 'post';
  });
  const [postMode, setPostMode] = useState('auto');
  const [imageMode, setImageMode] = useState(initialMode === 'pro' ? 'pro' : 'simple');
  const [blocks, setBlocks] = useState(() => {
    const fromDefaultPrompt = splitPromptIntoBlocks(normalizedDefaultPrompt);
    if (fromDefaultPrompt.length) {
      return fromDefaultPrompt;
    }
    const templateBlocks = buildEmptyBlocksFromTemplate();
    return templateBlocks.length ? templateBlocks : [''];
  });
  const [simplePromptText, setSimplePromptText] = useState(() => (
    initialSimplePrompt && initialSimplePrompt.trim()
      ? initialSimplePrompt
      : normalizedDefaultPrompt
  ));
  const [aspectRatio, setAspectRatio] = useState(initialAspectRatio);
  const [customWidth, setCustomWidth] = useState('1024');
  const [customHeight, setCustomHeight] = useState('1024');
  const [imageLoading, setImageLoading] = useState(false);
  const [promptImages, setPromptImages] = useState([]);
  const [useGeneratedImageAsBase, setUseGeneratedImageAsBase] = useState(false);
  const [showGallery, setShowGallery] = useState(null); // 'reference' | 'manual' | 'video-first-frame' | 'video-last-frame'
  const promptImageObjectUrlRef = useRef(new Set());
  const videoFirstFrameInputRef = useRef(null);
  const videoLastFrameInputRef = useRef(null);
  const [showPromptBlocksModal, setShowPromptBlocksModal] = useState(false);
  const [isRegeneratingBlocks, setIsRegeneratingBlocks] = useState(false);

  const [videoSimplePromptText, setVideoSimplePromptText] = useState('');
  const [videoAspectRatio, setVideoAspectRatio] = useState('9:16');
  const [videoResolution, setVideoResolution] = useState(VIDEO_DEFAULT_RESOLUTION);
  const [videoDuration, setVideoDuration] = useState(4);
  const [videoFps, setVideoFps] = useState(24);
  const [videoCount, setVideoCount] = useState(1);
  const [videoFirstFrame, setVideoFirstFrame] = useState(null);
  const [videoLastFrame, setVideoLastFrame] = useState(null);
  const [videoReferenceError, setVideoReferenceError] = useState('');
  const [videoReferenceUploading, setVideoReferenceUploading] = useState(false);
  const [videoReferenceVideos, setVideoReferenceVideos] = useState([]);
  const [videoReferenceVideoInput, setVideoReferenceVideoInput] = useState('');
  const [videoModels, setVideoModels] = useState([]);
  const [videoModelsLoading, setVideoModelsLoading] = useState(false);
  const [videoModelsError, setVideoModelsError] = useState('');
  const [selectedVideoModel, setSelectedVideoModel] = useState('');
  const [generatedVideo, setGeneratedVideo] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoJob, setVideoJob] = useState(null);
  const [videoStatus, setVideoStatus] = useState('');
  const [videoProgress, setVideoProgress] = useState(null);
  const [videoError, setVideoError] = useState('');
  const [videoDropdownOpen, setVideoDropdownOpen] = useState(null);
  const videoPollRef = useRef(null);
  const uploadsAlbumIdRef = useRef(null);
  const videoModelsLoadedRef = useRef(false);
  const videoModelsRequestRef = useRef(false);
  const videoModelsAttemptedRef = useRef(false);

  const [selectedStrategyId, setSelectedStrategyId] = useState(() => (
    initialStrategyId === null || initialStrategyId === undefined || initialStrategyId === ''
      ? null
      : `${initialStrategyId}`
  ));
  const [selectedDnaId, setSelectedDnaId] = useState(() => (
    initialDnaId === null || initialDnaId === undefined || initialDnaId === ''
      ? null
      : `${initialDnaId}`
  ));
  const hasAppliedDefaultDna = useRef(false);
  const [useDnaForImage, setUseDnaForImage] = useState(false);
  const [useStrategyForImage, setUseStrategyForImage] = useState(false);

  const [postPrompt, setPostPrompt] = useState('');
  const [useDnaForPost, setUseDnaForPost] = useState(Boolean(initialDnaId || (availableDnaProfiles && availableDnaProfiles.length)));
  const [useStrategyForPost, setUseStrategyForPost] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [autoPostLoading, setAutoPostLoading] = useState(false);

  const [manualMedia, setManualMedia] = useState([]);
  const manualMediaUrlsRef = useRef([]);
  const [manualVideoThumbnails, setManualVideoThumbnails] = useState({});
  const [manualThumbnailsLoading, setManualThumbnailsLoading] = useState({});
  const [manualDescription, setManualDescription] = useState('');
  const [descriptionLoading, setDescriptionLoading] = useState(false);
  const [manualImageGenerating, setManualImageGenerating] = useState(false);
  const [manualAIModalOpen, setManualAIModalOpen] = useState(false);
  const [manualAiModalType, setManualAiModalType] = useState('text'); // 'text' | 'image'
  const [manualLoading, setManualLoading] = useState(false);
  const [manualDraggedIndex, setManualDraggedIndex] = useState(null);
  const [manualDragOverIndex, setManualDragOverIndex] = useState(null);
  const [manualIsDragging, setManualIsDragging] = useState(false);
  const [manualIsOverTrash, setManualIsOverTrash] = useState(false);
  // Refs keep drag state in sync inside document-level listeners
  const manualIsDraggingRef = useRef(false);
  const manualDragOverIndexRef = useRef(null);
  const manualIsOverTrashRef = useRef(false);
  const manualGalleryScrollRef = useRef(0);
  const lastGalleryTypeRef = useRef(null);
  const manualDragStartRef = useRef({ x: 0, y: 0 });
  const [manualDragCurrentPos, setManualDragCurrentPos] = useState({ x: 0, y: 0 });
  const [manualDraggedImageDimensions, setManualDraggedImageDimensions] = useState({ width: 120, height: 150 });

  const [scheduledAt, setScheduledAt] = useState(null);
  const [scheduleError, setScheduleError] = useState('');
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const datePickerRef = useRef(null);
  const dateFieldRef = useRef(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [pickerMonth, setPickerMonth] = useState(() => new Date());
  const timeInputRef = useRef(null);
  const timePanelRef = useRef(null);
  const [isTimePickerOpen, setIsTimePickerOpen] = useState(false);
  const [hasInitialDataLoaded, setHasInitialDataLoaded] = useState(false);
  const isSchedulingBusy = autoPostLoading || manualLoading || isLoading || campaignsLoading || companyLoading;
  const [, setImageValidation] = useState('');
  const [, setPostValidation] = useState('');
  const [isStrategyDropdownOpen, setIsStrategyDropdownOpen] = useState(false);
  const strategyDropdownRef = useRef(null);
  const dnaToggleTouchedRef = useRef(false);

  const campaignsInput = useMemo(() => campaigns || [], [campaigns]);
  const availableStrategiesInput = useMemo(() => availableStrategies || [], [availableStrategies]);
  const availableDnaProfilesInput = useMemo(() => availableDnaProfiles || [], [availableDnaProfiles]);

  useEffect(() => {
    setCampaignOptions(campaignsInput);
  }, [campaignsInput]);

  useEffect(() => {
    setStrategyOptions(availableStrategiesInput);
  }, [availableStrategiesInput]);

  useEffect(() => {
    setDnaOptions(availableDnaProfilesInput);
  }, [availableDnaProfilesInput]);

  useEffect(() => {
    try {
      if (Array.isArray(fetchedCampaigns) && fetchedCampaigns.length) {
        setCampaignOptions(fetchedCampaigns);
        const derivedStrategies = extractStrategiesFromCampaigns(fetchedCampaigns, t);
        if (derivedStrategies.length) {
          setStrategyOptions((prev) => mergeUniqueById(prev, derivedStrategies));
        }
        setBootstrapError('');
      }
    } catch (error) {
      console.error('[ImageGenerationPage] Failed to map campaigns', error);
      setBootstrapError(error?.message || 'Errore nel caricamento delle campagne');
    }
  }, [fetchedCampaigns, t, language]);

  useEffect(() => {
    try {
      const companySource = currentCompany || (companyList && companyList[0]);
      const dnaProfile = extractDnaFromCompany(companySource, t);
      if (dnaProfile) {
        setDnaOptions((prev) => mergeUniqueById(prev, dnaProfile));
        setBootstrapError('');
      }
    } catch (error) {
      console.error('[ImageGenerationPage] Failed to derive DNA', error);
      setBootstrapError(error?.message || 'Errore nel caricamento del DNA');
    }
  }, [currentCompany, companyList, t, language]);

  const availableStrategyItems = useMemo(
    () => normalizeStrategyItems(strategyOptions, t),
    [strategyOptions, t, language]
  );
  const availableDnaItems = useMemo(
    () => normalizeDnaItems(dnaOptions, t),
    [dnaOptions, t, language]
  );

  const selectedStrategy = useMemo(() => {
    if (selectedStrategyId === null || selectedStrategyId === undefined || selectedStrategyId === '') return null;
    return availableStrategyItems.find((item) => `${item.id}` === `${selectedStrategyId}`) || null;
  }, [availableStrategyItems, selectedStrategyId]);

  const selectedDna = useMemo(() => {
    if (selectedDnaId === null || selectedDnaId === undefined || selectedDnaId === '') return null;
    return availableDnaItems.find((item) => `${item.id}` === `${selectedDnaId}`) || null;
  }, [availableDnaItems, selectedDnaId]);

  useEffect(() => {
    if (hasAppliedDefaultDna.current) return;
    if (initialDnaId !== null && initialDnaId !== undefined) return;
    if (!selectedDnaId && availableDnaItems.length > 0) {
      setSelectedDnaId(`${availableDnaItems[0].id}`);
      hasAppliedDefaultDna.current = true;
    }
  }, [availableDnaItems, initialDnaId, selectedDnaId]);

  const aspectRatioOptions = useMemo(() => ([
    { key: '1:1', label: '1:1' },
    { key: '16:9', label: '16:9' },
    { key: '9:16', label: '9:16' },
    { key: '4:3', label: '4:3' },
    { key: '3:4', label: '3:4' },
    { key: '21:9', label: '21:9' },
    { key: 'custom', label: t('imageGenerator.pro.options.custom', { defaultValue: 'Personalizzato' }) }
  ]), [t, language]);

  const videoModelOptions = useMemo(() => {
    const baseOptions = [
      { value: '', label: t('imageGenerator.video.settings.modelAuto', { defaultValue: 'Auto' }) }
    ];
    const modelOptions = videoModels
      .map((model) => {
        const name = model?.name || model?.id;
        if (!name) return null;
        const providerLabel = model.provider ? ` · ${model.provider}` : '';
        return { value: name, label: `${name}${providerLabel}` };
      })
      .filter(Boolean);
    return [...baseOptions, ...modelOptions];
  }, [t, videoModels]);

  const selectedVideoModelData = useMemo(() => {
    if (!selectedVideoModel) return null;
    return videoModels.find((model) => (
      model?.name === selectedVideoModel || model?.id === selectedVideoModel
    )) || null;
  }, [selectedVideoModel, videoModels]);

  const supportedVideoResolutions = useMemo(() => {
    const raw = getModelCapabilityList(selectedVideoModelData, [
      'resolutions',
      'resolution',
      'supported_resolutions',
      'supportedResolutions',
      'video_resolutions',
      'videoResolutions'
    ]);
    if (!Array.isArray(raw) || raw.length === 0) return null;
    const normalized = raw.map(normalizeVideoCapabilityValue).filter(Boolean);
    return normalized.length ? new Set(normalized) : null;
  }, [selectedVideoModelData]);

  const supportedVideoFps = useMemo(() => {
    const raw = getModelCapabilityList(selectedVideoModelData, [
      'fps',
      'frame_rates',
      'frameRates',
      'supported_fps',
      'supportedFps',
      'frame_rate'
    ]);
    if (!Array.isArray(raw) || raw.length === 0) return null;
    const normalized = raw
      .map((value) => normalizeVideoFpsValue(value))
      .filter((value) => value !== null);
    return normalized.length ? new Set(normalized) : null;
  }, [selectedVideoModelData]);

  const videoDurationOptions = useMemo(() => (
    VIDEO_DURATION_OPTIONS.map((value) => ({ value, label: `${value}s` }))
  ), []);

  const videoCountOptions = useMemo(() => (
    VIDEO_COUNT_OPTIONS.map((value) => ({ value, label: `${value}` }))
  ), []);

  const generatedImageState = useMemo(() => {
    if (!initialGeneratedImage) return null;
    if (typeof initialGeneratedImage === 'string') {
      return { id: 'initial', url: initialGeneratedImage, name: t('imageGenerator.defaults.generatedImageName', { defaultValue: 'Immagine generata' }) };
    }
    return {
      id: initialGeneratedImage.id || 'initial',
      url: initialGeneratedImage.url,
      name: initialGeneratedImage.name || t('imageGenerator.defaults.generatedImageName', { defaultValue: 'Immagine generata' })
    };
  }, [initialGeneratedImage, t, language]);

  const [generatedImage, setGeneratedImage] = useState(generatedImageState);
  const hasGeneratedImage = Boolean(generatedImage?.url);

  const buildMetadataHeader = useCallback(() => {
    const dimensions = aspectRatio === 'custom' ? `${customWidth}x${customHeight}` : aspectRatio;
    return t('imageGenerator.prompts.aspectRatio', { value: dimensions, defaultValue: `Formato: ${dimensions}` });
  }, [aspectRatio, customWidth, customHeight, t]);

  const buildPrompt = useCallback(() => {
    const joined = blocks.map((b) => b.trim()).filter(Boolean).join('\n');
    const header = buildMetadataHeader();
    return `${header}\n\n${joined}`.trim();
  }, [blocks, buildMetadataHeader]);

  const resolvedAspectRatio = useMemo(
    () => (aspectRatio === 'custom' ? resolveCustomRatio(customWidth, customHeight, '4:5') : aspectRatio),
    [aspectRatio, customWidth, customHeight]
  );

  const promptModeType = useMemo(() => (
    useGeneratedImageAsBase && generatedImage?.url ? 'modify' : 'generate'
  ), [generatedImage, useGeneratedImageAsBase]);
  const trimmedSimplePrompt = useMemo(() => simplePromptText.trim(), [simplePromptText]);
  const trimmedVideoPrompt = useMemo(() => videoSimplePromptText.trim(), [videoSimplePromptText]);

  const derivedPromptJson = useMemo(() => buildPromptJsonFromBlocks(blocks, {
    normalizedRatio: resolvedAspectRatio,
    simplePrompt: trimmedSimplePrompt,
    mode: promptModeType
  }), [blocks, promptModeType, resolvedAspectRatio, trimmedSimplePrompt]);

  const hasPromptJsonContent = useMemo(() => {
    if (!derivedPromptJson || typeof derivedPromptJson !== 'object') {
      return false;
    }
    return Object.keys(derivedPromptJson).some((key) => !RESERVED_PROMPT_JSON_KEYS.has(key) && derivedPromptJson[key]);
  }, [derivedPromptJson]);

  const resolveLabelForKey = useCallback((key) => {
    if (!key) {
      return '';
    }

    const normalized = key.toString().toLowerCase();
    const fallbackDefault = labelDefaults[normalized] || normalized
      .split('_')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');

    const translationKey = normalized.replace(/[^a-z0-9]+/g, '_');

    return t(`postReview.imageEditor.promptLabels.${translationKey}`, {
      defaultValue: fallbackDefault || key
    });
  }, [labelDefaults, t]);

  const buildImagePromptWithContext = useCallback(() => {
    const basePrompt = (imageMode === 'simple' ? trimmedSimplePrompt : buildPrompt()) || '';
    const contextParts = [];
    if (useStrategyForImage && selectedStrategy?.description) {
      contextParts.push(`${t('imageGenerator.settings.strategy.label', { defaultValue: 'Strategia' })}: ${selectedStrategy.description}`);
    }
    if (useDnaForImage && selectedDna?.description) {
      contextParts.push(`${t('imageGenerator.settings.dna.label', { defaultValue: 'DNA' })}: ${selectedDna.description}`);
    }
    return [basePrompt, contextParts.join('\n')].filter(Boolean).join('\n\n').trim();
  }, [
    imageMode,
    trimmedSimplePrompt,
    buildPrompt,
    selectedStrategy,
    selectedDna,
    useStrategyForImage,
    useDnaForImage,
    t,
    language
  ]);

  const handleBlockChange = useCallback((idx, value) => {
    setBlocks((prev) => prev.map((b, i) => (i === idx ? value : b)));
  }, []);

  const showPostAlert = useCallback((message, variant = 'warning') => {
    setPostValidation(message);
    if (notify) {
      notify({ variant, title: message });
    }
  }, [notify]);

  const showImageAlert = useCallback((message, variant = 'warning') => {
    setImageValidation(message);
    if (notify) {
      notify({ variant, title: message });
    }
  }, [notify]);

  const confirmAction = useCallback(async (message) => {
    if (mobileConfirm) {
      const result = await mobileConfirm({
        title: message,
        confirmText: t('imageGenerator.actions.confirm', { defaultValue: 'Conferma' }),
        cancelText: t('imageGenerator.actions.cancel', { defaultValue: 'Annulla' }),
        variant: 'warning'
      });
      return result;
    }
    if (typeof window !== 'undefined') {
      return window.confirm(message);
    }
    return true;
  }, [mobileConfirm, t]);

  const formatTimeForInput = useCallback((dateValue) => {
    if (!dateValue) return '12:00';
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '12:00';
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }, []);

  const isSameDay = useCallback((dateA, dateB) => {
    if (!dateA || !dateB) return false;
    return (
      dateA.getFullYear() === dateB.getFullYear() &&
      dateA.getMonth() === dateB.getMonth() &&
      dateA.getDate() === dateB.getDate()
    );
  }, []);

  const today = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  }, []);

  useEffect(() => {
    if (!scheduledAt) return;
    const parsed = new Date(scheduledAt);
    if (isNaN(parsed.getTime())) return;
    setSelectedDate(parsed);
    setPickerMonth(parsed);
    setSelectedTime(formatTimeForInput(parsed));
  }, [scheduledAt, formatTimeForInput]);

  const combineDateAndTime = useCallback((dateValue, timeValue) => {
    if (!dateValue || !timeValue) return null;
    const [hours, minutes] = timeValue.split(':').map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return null;
    }

    const combinedDate = new Date(dateValue);
    combinedDate.setHours(hours, minutes, 0, 0);

    if (isNaN(combinedDate.getTime())) {
      return null;
    }

    setScheduledAt(combinedDate);
    setScheduleError('');
    setPostValidation('');
    return combinedDate;
  }, []);

  const handleDateSelect = useCallback((newDate) => {
    if (!newDate) return;
    const normalized = new Date(newDate);
    normalized.setHours(0, 0, 0, 0);
    if (normalized < today) {
      return;
    }
    setSelectedDate(newDate);
    setPickerMonth(newDate);
    const timeToUse = selectedTime || formatTimeForInput(scheduledAt);
    const resolvedTime = timeToUse || '12:00';
    setSelectedTime(resolvedTime);
    combineDateAndTime(newDate, resolvedTime);
  }, [today, selectedTime, formatTimeForInput, scheduledAt, combineDateAndTime]);

  const hourOptions = useMemo(() => Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')), []);
  const minuteOptions = useMemo(() => Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0')), []);

  const handleTimeInputChange = useCallback((timeValue) => {
    if (!timeValue) return;
    const finalTime = timeValue.slice(0, 5);
    setSelectedTime(finalTime);
    if (!selectedDate) {
      setScheduleError('');
      return;
    }
    combineDateAndTime(selectedDate, finalTime);
    setIsTimePickerOpen(false);
  }, [selectedDate, combineDateAndTime]);

  const handleHourSelect = useCallback((hour) => {
    const [, minutes = '00'] = (selectedTime || '12:00').split(':');
    handleTimeInputChange(`${hour}:${minutes}`);
  }, [handleTimeInputChange, selectedTime]);

  const handleMinuteSelect = useCallback((minute) => {
    const [hours = '12'] = (selectedTime || '12:00').split(':');
    handleTimeInputChange(`${hours}:${minute}`);
  }, [handleTimeInputChange, selectedTime]);

  const displayValue = useMemo(() => {
    if (!selectedDate) return t('postReview.postEdit.time.placeholder', { defaultValue: 'Scegli data e ora' });
    const datePart = selectedDate.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' });
    return `${datePart} · ${selectedTime || '—:—'}`;
  }, [selectedDate, selectedTime, t]);

  const openTimePicker = useCallback(() => {
    const el = timeInputRef.current;
    if (el) {
      el.focus();
    }
    setIsTimePickerOpen(true);
  }, []);

  useEffect(() => {
    if (!isTimePickerOpen) return undefined;
    const handleClickOutside = (event) => {
      if (timePanelRef.current?.contains(event.target) || timeInputRef.current?.contains(event.target)) {
        return;
      }
      setIsTimePickerOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isTimePickerOpen]);

  const goToPreviousMonth = useCallback(() => {
    setPickerMonth((prev) => {
      const next = new Date(prev);
      next.setMonth(prev.getMonth() - 1);
      return next;
    });
  }, []);

  const goToNextMonth = useCallback(() => {
    setPickerMonth((prev) => {
      const next = new Date(prev);
      next.setMonth(prev.getMonth() + 1);
      return next;
    });
  }, []);

  const weekdayLabels = useMemo(() => ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'], []);

  const calendarDays = useMemo(() => {
    const start = new Date(pickerMonth.getFullYear(), pickerMonth.getMonth(), 1);
    const leadingEmpty = (start.getDay() + 6) % 7;
    const daysInMonth = new Date(pickerMonth.getFullYear(), pickerMonth.getMonth() + 1, 0).getDate();
    const days = [];

    for (let i = 0; i < leadingEmpty; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(pickerMonth.getFullYear(), pickerMonth.getMonth(), day));
    }

    while (days.length % 7 !== 0) {
      days.push(null);
    }

    return days;
  }, [pickerMonth]);

  const monthLabel = useMemo(
    () => pickerMonth.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' }),
    [pickerMonth]
  );

  useEffect(() => {
    if (!isDatePickerOpen) return undefined;

    const handleClickOutside = (event) => {
      if (datePickerRef.current?.contains(event.target) || dateFieldRef.current?.contains(event.target)) {
        return;
      }
      setIsDatePickerOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isDatePickerOpen]);

  useEffect(() => {
    if (!scheduledAt) {
      setScheduleError('');
      return;
    }
    if (scheduledAt.getTime() < Date.now()) {
      setScheduleError(t('imageGenerator.schedule.invalid', { defaultValue: 'Non puoi selezionare una data passata' }));
    } else {
      setScheduleError('');
    }
  }, [scheduledAt, t]);

  const revokePromptImageUrl = useCallback((url) => {
    if (!url) return;
    try {
      URL.revokeObjectURL(url);
    } catch (error) {
      console.warn('[ImageGenerationPage] Failed to revoke object URL', error);
    }
  }, []);

  const handlePromptImageInputChange = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (promptImages.length >= MAX_REFERENCE_IMAGES) {
      showImageAlert(t('imageGenerator.reference.limit', { defaultValue: 'Puoi aggiungere fino a 3 immagini di riferimento.' }));
      event.target.value = '';
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    promptImageObjectUrlRef.current.add(previewUrl);
    setPromptImages((prev) => [...prev, {
      id: previewUrl,
      url: previewUrl,
      name: file.name || t('imageGenerator.defaults.referenceImageName', { defaultValue: 'Riferimento' }),
      file,
      objectUrl: previewUrl
    }]);

    event.target.value = '';
  }, [promptImages, showImageAlert, t, language]);

  const handleDeletePromptImage = useCallback((id) => {
    setPromptImages((prev) => {
      const target = prev.find((img) => img.id === id);
      if (target?.objectUrl) {
        promptImageObjectUrlRef.current.delete(target.objectUrl);
        revokePromptImageUrl(target.objectUrl);
      }
      return prev.filter((img) => img.id !== id);
    });
  }, [revokePromptImageUrl]);

  const openGallery = useCallback((type) => {
    if (type === 'manual' && typeof window !== 'undefined') {
      manualGalleryScrollRef.current = window.scrollY || 0;
    }
    lastGalleryTypeRef.current = type;
    setShowGallery(type);
  }, []);

  const closeGallery = useCallback(() => {
    const lastType = lastGalleryTypeRef.current;
    setShowGallery(null);
    if (lastType === 'manual' && typeof window !== 'undefined') {
      const target = manualGalleryScrollRef.current || 0;
      const scrollToTarget = () => window.scrollTo({ top: target, behavior: 'auto' });
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(scrollToTarget);
      } else {
        scrollToTarget();
      }
    }
  }, []);

  const handleReferenceSelect = useCallback((image) => {
    if (promptImages.length >= MAX_REFERENCE_IMAGES) {
      showImageAlert(t('imageGenerator.reference.limit', { defaultValue: 'Puoi aggiungere fino a 3 immagini di riferimento.' }));
      return;
    }

    if (image?.files && image.files.length > 0) {
      const file = image.files[0];
      const previewUrl = URL.createObjectURL(file);
      promptImageObjectUrlRef.current.add(previewUrl);
      setPromptImages((prev) => [...prev, {
        id: previewUrl,
        url: previewUrl,
        name: file.name || t('imageGenerator.defaults.referenceImageName', { defaultValue: 'Riferimento' }),
        file,
        objectUrl: previewUrl
      }]);
    } else if (image) {
      const sourceUrl = image.selectedUrl || image.url || image.displayUrl || image.raw?.public_url || image.raw?.url || image.raw?.file_url;
      if (!sourceUrl) {
        closeGallery();
        return;
      }
      setPromptImages((prev) => [...prev, {
        id: image.id || sourceUrl,
        url: sourceUrl,
        name: image.name || image.title || image.filename || t('imageGenerator.defaults.referenceImageName', { defaultValue: 'Riferimento' })
      }]);
    }
    closeGallery();
  }, [closeGallery, promptImages, showImageAlert, t, language]);

  const handleVideoFrameSelect = useCallback((image, frameType) => {
    if (!image) {
      closeGallery();
      return;
    }
    const sourceUrl = image.selectedUrl || image.url || image.displayUrl || image.raw?.public_url || image.raw?.url || image.raw?.file_url;
    if (!sourceUrl) {
      closeGallery();
      return;
    }
    const payload = {
      id: generateMediaId(),
      name: image.name || image.title || image.filename || t('imageGenerator.defaults.referenceImageName', { defaultValue: 'Riferimento' }),
      url: sourceUrl,
      raw: image
    };
    if (frameType === 'first') {
      setVideoFirstFrame(payload);
    } else {
      setVideoLastFrame(payload);
    }
    setVideoReferenceError('');
    closeGallery();
  }, [closeGallery, t]);

  useEffect(() => () => {
    if (promptImageObjectUrlRef.current?.size) {
      promptImageObjectUrlRef.current.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch (error) {
          console.warn('[ImageGenerationPage] Failed to revoke object URL on unmount', error);
        }
      });
      promptImageObjectUrlRef.current.clear();
    }
    manualMediaUrlsRef.current.forEach((url) => {
      try {
        URL.revokeObjectURL(url);
      } catch (error) {
        console.warn('[ImageGenerationPage] Failed to revoke manual media URL', error);
      }
    });
    manualMediaUrlsRef.current = [];
  }, []);

  useEffect(() => {
    if (selectedStrategyId && !selectedStrategy) {
      setSelectedStrategyId(null);
    }
  }, [selectedStrategyId, selectedStrategy]);

  useEffect(() => {
    if (selectedDnaId && !selectedDna) {
      setSelectedDnaId(null);
    }
  }, [selectedDnaId, selectedDna]);

  useEffect(() => {
    if (!selectedDna) {
      if (!dnaToggleTouchedRef.current) {
        setUseDnaForPost(false);
      }
    } else if (!dnaToggleTouchedRef.current) {
      setUseDnaForPost(true);
    }
  }, [selectedDna]);

  useEffect(() => {
    // clear validations when changing macro context
    setImageValidation('');
    setPostValidation('');
    if (activeTab !== 'video') {
      setVideoError('');
      setVideoReferenceError('');
      setVideoDropdownOpen(null);
    }
  }, [activeTab, postMode, imageMode]);

  useEffect(() => {
    if (imageMode === 'pro' && useGeneratedImageAsBase) {
      setUseGeneratedImageAsBase(false);
    }
  }, [imageMode, useGeneratedImageAsBase]);

  useEffect(() => {
    if (!isLoading && !campaignsLoading && !companyLoading) {
      setHasInitialDataLoaded(true);
    }
  }, [isLoading, campaignsLoading, companyLoading]);

  useEffect(() => {
    if (!supportedVideoResolutions) return;
    const normalizedCurrent = normalizeVideoCapabilityValue(videoResolution);
    if (normalizedCurrent && supportedVideoResolutions.has(normalizedCurrent)) return;
    const fallback = VIDEO_RESOLUTION_OPTIONS.find((option) => (
      supportedVideoResolutions.has(normalizeVideoCapabilityValue(option))
    ));
    if (fallback) {
      setVideoResolution(fallback);
    }
  }, [supportedVideoResolutions, videoResolution]);

  useEffect(() => {
    if (!supportedVideoFps) return;
    const normalizedCurrent = normalizeVideoFpsValue(videoFps);
    if (normalizedCurrent !== null && supportedVideoFps.has(normalizedCurrent)) return;
    const fallback = VIDEO_FPS_OPTIONS.find((option) => supportedVideoFps.has(option.value));
    if (fallback) {
      setVideoFps(fallback.value);
    }
  }, [supportedVideoFps, videoFps]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (strategyDropdownRef.current && !strategyDropdownRef.current.contains(event.target)) {
        setIsStrategyDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (activeTab !== 'video' || videoModelsLoadedRef.current || videoModelsRequestRef.current || videoModelsAttemptedRef.current) {
      return undefined;
    }

    let isCancelled = false;
    videoModelsRequestRef.current = true;
    videoModelsAttemptedRef.current = true;
    setVideoModelsLoading(true);
    setVideoModelsError('');

    const loadVideoModels = async () => {
      try {
        const response = await apiClient.get('/api/v1/ai/video-models');
        if (isCancelled) return;
        const models = Array.isArray(response?.models) ? response.models : [];
        const normalized = models.map((model) => ({
          id: model?.model_name || model?.name,
          name: model?.model_name || model?.name,
          provider: model?.provider || model?.provider_key,
          description: model?.description || '',
          capabilities: model?.capabilities || {},
          metadata: model?.metadata || {}
        })).filter((model) => model.id);

        setVideoModels(normalized);
        if (!selectedVideoModel && normalized.length > 0) {
          setSelectedVideoModel(normalized[0].name);
        }
        videoModelsLoadedRef.current = true;
      } catch (error) {
        if (!isCancelled) {
          console.error('[ImageGenerationPage] Failed to load video models', error);
          setVideoModelsError(error?.message || t('imageGenerator.video.models.error', { defaultValue: 'Impossibile caricare i modelli video.' }));
        }
      } finally {
        if (!isCancelled) {
          setVideoModelsLoading(false);
        }
        videoModelsRequestRef.current = false;
      }
    };

    loadVideoModels();
    return () => {
      isCancelled = true;
      videoModelsRequestRef.current = false;
    };
  }, [activeTab, apiClient.get, t]);

  useEffect(() => {
    if (activeTab !== 'video') {
      videoModelsAttemptedRef.current = false;
      if (videoModelsLoading) {
        setVideoModelsLoading(false);
      }
    }
  }, [activeTab, videoModelsLoading]);

  const handleImageGenerate = useCallback(async () => {
    const promptToSend = (buildImagePromptWithContext() || '').trim();
    if (!promptToSend) {
      showImageAlert(t('imageGenerator.status.error', { defaultValue: 'Inserisci un prompt prima di generare.' }));
      return;
    }

    if (promptToSend.length < PROMPT_MIN_LENGTH) {
      showImageAlert(t('imageGenerator.status.shortPrompt', { defaultValue: 'Il prompt deve contenere almeno 10 caratteri.' }));
      return;
    }

    const baseUserPrompt = imageMode === 'simple' ? trimmedSimplePrompt : promptToSend;
    if (!baseUserPrompt || baseUserPrompt.length < PROMPT_MIN_LENGTH) {
      showImageAlert(t('imageGenerator.status.shortPrompt', { defaultValue: 'Il prompt deve contenere almeno 10 caratteri.' }));
      return;
    }

    const referenceImages = [];
    if (useGeneratedImageAsBase && generatedImage?.url) {
      referenceImages.push(generatedImage.url);
    }
    promptImages.forEach((img) => {
      if (img?.url) {
        referenceImages.push(img.url);
      }
    });
    const resolvedReferenceImages = referenceImages
      .map(toAbsoluteHttpUrl)
      .filter(Boolean);

    const resolvedDnaId = resolveDnaId(selectedDna);
    const cappedPromptText = promptToSend.length > PROMPT_MAX_LENGTH ? promptToSend.slice(0, PROMPT_MAX_LENGTH) : promptToSend;
    const cappedUserPrompt = baseUserPrompt.length > PROMPT_MAX_LENGTH ? baseUserPrompt.slice(0, PROMPT_MAX_LENGTH) : baseUserPrompt;
    const baseReferenceIncluded = Boolean(useGeneratedImageAsBase && generatedImage?.url);

    const payload = {
      prompt: cappedPromptText,
      mode: imageMode,
      simplePrompt: simplePromptText,
      blocks,
      aspectRatio,
      customWidth,
      customHeight,
      referenceImages: resolvedReferenceImages,
      referenceImage: promptImages[0] || null,
      strategyId: useStrategyForImage && selectedStrategy ? selectedStrategy.id : null,
      dnaId: useDnaForImage && resolvedDnaId ? resolvedDnaId : null,
      baseImage: baseReferenceIncluded
        ? {
            id: generatedImage.id,
            url: generatedImage.url,
            name: generatedImage.name || t('imageGenerator.defaults.referenceImageName', { defaultValue: 'Riferimento' })
          }
        : null
    };

    const apiEndpoint = '/api/v1/ai/generate-image-new';
    const apiPayload = {
      prompt_text: cappedPromptText,
      user_prompt: cappedUserPrompt,
      count: 1,
      ratio: resolvedAspectRatio
    };
    if (resolvedReferenceImages.length > 0) {
      apiPayload.reference_images = resolvedReferenceImages;
    }

    if (hasPromptJsonContent) {
      const promptJson = buildPromptJsonFromBlocks(blocks, {
        normalizedRatio: resolvedAspectRatio,
        simplePrompt: trimmedSimplePrompt || undefined,
        referenceImages: resolvedReferenceImages,
        mode: promptModeType,
        baseReferenceIncluded,
        includeReferenceStats: true
      });
      const promptJsonSchema = buildPromptJsonSchema(promptJson);
      const hasJsonPayload = Object.keys(promptJson).some((key) => !RESERVED_PROMPT_JSON_KEYS.has(key) && promptJson[key]);

      if (hasJsonPayload) {
        apiPayload.prompt_json = promptJson;
        if (promptJsonSchema) {
          apiPayload.prompt_json_schema = promptJsonSchema;
        }
      }
    }

    setImageValidation('');
    setImageLoading(true);
    try {
      if (onGenerate) {
        const result = await onGenerate(payload);
        if (result?.imageUrl || result?.url) {
          setGeneratedImage({
            id: result.imageId || result.id || `generated-${Date.now()}`,
            url: result.imageUrl || result.url,
            name: result.imageName || t('imageGenerator.defaults.generatedImageName', { defaultValue: 'Immagine generata' })
          });
          return;
        }
      }

      const response = await apiClient.post(apiEndpoint, apiPayload);
      const urlCandidates = response?.regenerated_images
        || response?.regenerated_content?.media_urls
        || (Array.isArray(response?.images) ? response.images.map((img) => img?.url || img?.local_url) : undefined)
        || (Array.isArray(response?.results?.images) ? response.results.images.map((img) => img?.url || img?.local_url) : undefined);
      const firstUrl = Array.isArray(urlCandidates) ? urlCandidates.find(Boolean) : null;
      const imageEntry = response?.images?.[0] || response?.results?.images?.[0];
      const mediaIds = response?.regenerated_content?.media_file_ids;
      const nextImage = firstUrl
        ? {
            id: (Array.isArray(mediaIds) && mediaIds[0]) || imageEntry?.id || imageEntry?._id || `generated-${Date.now()}`,
            url: firstUrl,
            name: imageEntry?.name || imageEntry?.filename || t('imageGenerator.defaults.generatedImageName', { defaultValue: 'Immagine generata' })
          }
        : null;

      if (nextImage) {
        setGeneratedImage(nextImage);
      } else {
        setImageValidation(t('imageGenerator.status.error', { defaultValue: 'Errore nella generazione, riprova.' }));
      }
    } catch (error) {
      console.error('[ImageGenerationPage] Generate failed', error);
      showImageAlert(error?.message || t('imageGenerator.status.error', { defaultValue: 'Errore nella generazione, riprova.' }), 'error');
    } finally {
      setImageLoading(false);
    }
  }, [
    onGenerate,
    buildImagePromptWithContext,
    imageMode,
    simplePromptText,
    blocks,
    aspectRatio,
    customWidth,
    customHeight,
    promptImages,
    selectedStrategy,
    selectedDna,
    useStrategyForImage,
    useDnaForImage,
    generatedImage,
    useGeneratedImageAsBase,
    t,
    language,
    apiClient,
    resolvedAspectRatio,
    showImageAlert,
    trimmedSimplePrompt,
    hasPromptJsonContent,
    promptModeType
  ]);

  const stopVideoPolling = useCallback(() => {
    if (videoPollRef.current && typeof window !== 'undefined') {
      window.clearTimeout(videoPollRef.current);
      videoPollRef.current = null;
    }
  }, []);

  useEffect(() => () => {
    stopVideoPolling();
  }, [stopVideoPolling]);

  const normalizeVideoResults = useCallback((job) => {
    const items = Array.isArray(job?.result?.videos) ? job.result.videos : [];
    const normalized = items.map((entry) => {
      if (!entry) return null;
      const urlCandidate = entry.public_url || entry.local_url || entry.url;
      const resolvedUrl = toAbsoluteHttpUrl(urlCandidate) || urlCandidate;
      if (!resolvedUrl) return null;
      return {
        id: entry.media_file_id || entry.filename || generateMediaId(),
        url: resolvedUrl,
        name: entry.filename || t('imageGenerator.video.defaults.generatedVideoName', { defaultValue: 'Video generato' }),
        durationSeconds: entry.duration_seconds,
        fps: entry.fps,
        width: entry.width,
        height: entry.height,
        format: entry.format,
        raw: entry
      };
    }).filter(Boolean);

    setGeneratedVideo(normalized[0] || null);
  }, [t]);

  const updateVideoJobState = useCallback((job) => {
    if (!job) return;
    setVideoJob(job);
    setVideoStatus(job.status || '');
    const progressValue = typeof job.progress === 'number' ? Math.max(0, Math.min(1, job.progress)) : null;
    setVideoProgress(progressValue);

    if (job.status === 'succeeded') {
      normalizeVideoResults(job);
      setVideoLoading(false);
      setVideoError('');
      return;
    }

    if (job.status === 'queued' || job.status === 'running') {
      setVideoError('');
      setVideoLoading(true);
      return;
    }

    if (job.status === 'failed' || job.status === 'cancelled') {
      setVideoLoading(false);
      const fallbackMessage = job.status === 'cancelled'
        ? t('imageGenerator.video.status.cancelled', { defaultValue: 'Generazione annullata.' })
        : t('imageGenerator.video.status.error', { defaultValue: 'Errore nella generazione video.' });
      const errorMessage = job?.error?.message || job?.error || fallbackMessage;
      setVideoError(errorMessage);
    }
  }, [normalizeVideoResults, t]);

  const scheduleVideoPoll = useCallback((jobId) => {
    if (!jobId || typeof window === 'undefined') return;
    stopVideoPolling();

    videoPollRef.current = window.setTimeout(async () => {
      try {
        const response = await apiClient.get(`/api/v1/ai/video-jobs/${jobId}`);
        updateVideoJobState(response);

        if (response?.status === 'queued' || response?.status === 'running') {
          scheduleVideoPoll(jobId);
        } else {
          stopVideoPolling();
        }
      } catch (error) {
        console.error('[ImageGenerationPage] Video job poll failed', error);
        setVideoError(error?.message || t('imageGenerator.video.status.error', { defaultValue: 'Errore nella generazione video.' }));
        stopVideoPolling();
        setVideoLoading(false);
      }
    }, 2500);
  }, [apiClient, stopVideoPolling, t, updateVideoJobState]);

  const handleGenerateVideo = useCallback(async () => {
    if (videoReferenceUploading) {
      showImageAlert(t('imageGenerator.video.validation.uploading', { defaultValue: 'Attendi il completamento degli upload.' }));
      return;
    }
    const basePrompt = trimmedVideoPrompt;
    if (!basePrompt || basePrompt.trim().length < VIDEO_PROMPT_MIN_LENGTH) {
      showImageAlert(t('imageGenerator.video.validation.prompt', { defaultValue: 'Descrivi il video da generare.' }));
      return;
    }
    if (basePrompt.length > VIDEO_PROMPT_MAX_LENGTH) {
      showImageAlert(t('imageGenerator.video.status.tooLong', { defaultValue: 'Il prompt è troppo lungo.' }));
      return;
    }

    const durationSeconds = parseInt(videoDuration, 10);
    if (!durationSeconds || durationSeconds < 1 || durationSeconds > 30) {
      showImageAlert(t('imageGenerator.video.validation.duration', { defaultValue: 'Durata tra 1 e 30 secondi.' }));
      return;
    }

    const fpsValue = parseInt(videoFps, 10);
    if (!fpsValue || fpsValue < 1 || fpsValue > 60) {
      showImageAlert(t('imageGenerator.video.validation.fps', { defaultValue: 'FPS tra 1 e 60.' }));
      return;
    }

    const countValue = parseInt(videoCount, 10);
    if (!countValue || countValue < 1 || countValue > 4) {
      showImageAlert(t('imageGenerator.video.validation.count', { defaultValue: 'Genera da 1 a 4 video.' }));
      return;
    }

    const ratioValue = VIDEO_RATIO_OPTIONS.includes(videoAspectRatio) ? videoAspectRatio : VIDEO_RATIO_OPTIONS[0];
    const resolutionValue = (videoResolution || VIDEO_DEFAULT_RESOLUTION).trim();

    const resolvedReferenceImages = [];
    const seenReferenceUrls = new Set();
    [videoFirstFrame?.url, videoLastFrame?.url]
      .map(toAbsoluteHttpUrl)
      .filter(Boolean)
      .forEach((url) => {
        if (seenReferenceUrls.has(url)) return;
        seenReferenceUrls.add(url);
        resolvedReferenceImages.push(url);
      });

    const resolvedReferenceVideos = [];
    const seenVideoUrls = new Set();
    videoReferenceVideos
      .map(toAbsoluteHttpUrl)
      .filter(Boolean)
      .forEach((url) => {
        if (seenVideoUrls.has(url)) return;
        seenVideoUrls.add(url);
        resolvedReferenceVideos.push(url);
      });

    const apiPayload = {
      prompt: basePrompt.trim(),
      duration_seconds: durationSeconds,
      count: countValue,
      ratio: ratioValue,
      resolution: resolutionValue,
      fps: fpsValue
    };

    if (selectedVideoModel) {
      apiPayload.model = selectedVideoModel;
    }
    if (resolvedReferenceImages.length) {
      apiPayload.reference_images = resolvedReferenceImages;
    }
    if (resolvedReferenceVideos.length) {
      apiPayload.reference_videos = resolvedReferenceVideos;
    }

    setVideoLoading(true);
    setVideoError('');
    setVideoProgress(null);
    setVideoJob(null);
    setVideoStatus('');
    stopVideoPolling();

    try {
      if (typeof apiClient.clearError === 'function') {
        apiClient.clearError();
      }
      const response = await apiClient.post('/api/v1/ai/generate-videos', apiPayload);
      const job = response?.job || response;
      if (!job?.id) {
        throw new Error(t('imageGenerator.video.status.error', { defaultValue: 'Errore nella generazione video.' }));
      }

      updateVideoJobState(job);
      if (job.status === 'queued' || job.status === 'running') {
        scheduleVideoPoll(job.id);
      }
      if (job.status === 'succeeded') {
        setVideoLoading(false);
      }
    } catch (error) {
      console.error('[ImageGenerationPage] Video generation failed', error);
      const resolvedMessage = (() => {
        if (error?.status === 402) {
          return t('imageGenerator.video.status.credits', { defaultValue: 'Crediti AI insufficienti.' });
        }
        if (error?.status === 429) {
          return t('imageGenerator.video.status.rateLimit', { defaultValue: 'Troppi tentativi. Riprova tra poco.' });
        }
        if (error?.status === 400) {
          return t('imageGenerator.video.status.invalid', { defaultValue: 'Parametri non validi per la generazione.' });
        }
        return error?.message || t('imageGenerator.video.status.error', { defaultValue: 'Errore nella generazione video.' });
      })();
      setVideoError(resolvedMessage);
      showImageAlert(resolvedMessage, 'error');
      setVideoLoading(false);
    }
  }, [
    apiClient,
    scheduleVideoPoll,
    selectedVideoModel,
    showImageAlert,
    stopVideoPolling,
    t,
    trimmedVideoPrompt,
    updateVideoJobState,
    videoAspectRatio,
    videoCount,
    videoDuration,
    videoFps,
    videoFirstFrame,
    videoLastFrame,
    videoReferenceVideos,
    videoReferenceUploading,
    videoResolution
  ]);

  const handleCancelVideoJob = useCallback(async () => {
    if (!videoJob?.id) return;
    try {
      const response = await apiClient.post(`/api/v1/ai/video-jobs/${videoJob.id}/cancel`, {});
      updateVideoJobState(response);
      stopVideoPolling();
      setVideoLoading(false);
    } catch (error) {
      console.error('[ImageGenerationPage] Video cancel failed', error);
      setVideoError(error?.message || t('imageGenerator.video.status.error', { defaultValue: 'Errore nella generazione video.' }));
    }
  }, [apiClient, stopVideoPolling, t, updateVideoJobState, videoJob?.id]);

  const isResolutionOptionDisabled = useCallback((option) => {
    if (!supportedVideoResolutions) return false;
    const normalized = normalizeVideoCapabilityValue(option);
    return !supportedVideoResolutions.has(normalized);
  }, [supportedVideoResolutions]);

  const isFpsOptionDisabled = useCallback((value) => {
    if (!supportedVideoFps) return false;
    const normalized = normalizeVideoFpsValue(value);
    if (normalized === null) return false;
    return !supportedVideoFps.has(normalized);
  }, [supportedVideoFps]);

  const handleClosePromptBlocksModal = useCallback(() => {
    if (isRegeneratingBlocks) {
      return;
    }
    setShowPromptBlocksModal(false);
  }, [isRegeneratingBlocks]);

  const handlePromptBlocksRegeneration = useCallback(async ({ prompt: modalPrompt }) => {
    const trimmedInstructions = (modalPrompt || '').trim();

    if (!trimmedInstructions) {
      if (notify) {
        notify({
          variant: 'warning',
          title: t('postReview.imageEditor.alerts.missingPrompt', { defaultValue: 'Inserisci delle istruzioni per rigenerare i blocchi.' })
        });
      }
      return;
    }

    setIsRegeneratingBlocks(true);

    try {
      if (typeof apiClient.clearError === 'function') {
        apiClient.clearError();
      }

      const metadataHeader = buildMetadataHeader();
      const enrichedInstructions = [
        trimmedInstructions,
        metadataHeader
      ].filter(Boolean).join('\n\n');

      const payload = {
        prompt_text: trimmedInstructions
      };

      if (enrichedInstructions) {
        payload.user_prompt = enrichedInstructions;
      }

      if (hasPromptJsonContent) {
        const promptJsonSchema = buildPromptJsonSchema(derivedPromptJson);
        if (promptJsonSchema) {
          payload.prompt_json_schema = promptJsonSchema;
        }
      }

      const response = await apiClient.post(
        '/api/v1/posts/prompt-json',
        payload
      );

      const resolvedPromptJson = response?.prompt_json || null;
      const resolvedPromptText = response?.prompt_text || '';

      if (resolvedPromptJson) {
        const regeneratedBlocks = buildBlocksFromPromptJson(resolvedPromptJson, resolveLabelForKey);
        if (regeneratedBlocks.length > 0) {
          setBlocks(regeneratedBlocks);
        }
      } else if (resolvedPromptText) {
        const regeneratedBlocks = splitPromptIntoBlocks(resolvedPromptText);
        if (regeneratedBlocks.length > 0) {
          setBlocks(regeneratedBlocks);
        }
      }

      setShowPromptBlocksModal(false);
      if (notify) {
        notify({
          variant: 'success',
          title: t('postReview.imageEditor.alerts.blocksSuccess', { defaultValue: 'Blocchi rigenerati! Ricordati di salvare le modifiche.' })
        });
      }
    } catch (error) {
      console.error('[ImageGenerationPage] Prompt blocks regeneration failed', error);
      const message = error?.message || t('postReview.imageEditor.alerts.blocksError', { defaultValue: 'Impossibile rigenerare i blocchi del prompt. Riprova.' });
      if (notify) {
        notify({
          variant: 'error',
          title: message
        });
      }
    } finally {
      setIsRegeneratingBlocks(false);
    }
  }, [
    apiClient,
    buildMetadataHeader,
    derivedPromptJson,
    hasPromptJsonContent,
    notify,
    resolveLabelForKey,
    t
  ]);

  const handleStrategySelect = useCallback((value) => {
    if (value === '' || value === null || value === undefined) {
      setSelectedStrategyId(null);
    } else {
      setSelectedStrategyId(`${value}`);
    }
    setIsStrategyDropdownOpen(false);
  }, []);

  const ensureUploadsAlbumId = useCallback(async () => {
    if (uploadsAlbumIdRef.current) {
      return uploadsAlbumIdRef.current;
    }

    const params = new URLSearchParams({
      page: '1',
      page_size: '50',
      include_archived: 'false'
    });

    const response = await apiClient.get(`/api/v1/media/albums?${params.toString()}`);
    const items = Array.isArray(response?.items) ? response.items : [];
    const matchingAlbum = items
      .map((entry) => entry?.album || entry?.data?.album || entry)
      .find((album) => (album?.metadata?.slug || '').toLowerCase() === 'uploads');

    const albumId = matchingAlbum?.id || matchingAlbum?._id || matchingAlbum?.album_id || matchingAlbum?.albumId;
    if (albumId) {
      uploadsAlbumIdRef.current = albumId;
      return albumId;
    }

    const payload = {
      title: t('imageGenerator.video.uploadsAlbumTitle', { defaultValue: 'Uploads' }),
      description: t('imageGenerator.video.uploadsAlbumDescription', { defaultValue: 'Upload temporanei per reference AI.' }),
      metadata: { slug: 'uploads', system: true }
    };
    const createResponse = await apiClient.post('/api/v1/media/albums', payload);
    const createdAlbum = createResponse?.album || createResponse?.data?.album || createResponse;
    const createdId = createdAlbum?.id || createdAlbum?._id || createdAlbum?.album_id || createdAlbum?.albumId;
    if (!createdId) {
      throw new Error(t('imageGenerator.video.uploadsAlbumError', { defaultValue: "Impossibile creare l'album uploads." }));
    }

    uploadsAlbumIdRef.current = createdId;
    return createdId;
  }, [apiClient, t]);

  const uploadReferenceImageToUploads = useCallback(async (file) => {
    const albumId = await ensureUploadsAlbumId();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name || 'reference');
    formData.append('media_type', 'image');
    formData.append('subtype', 'uploaded');
    formData.append('size_kb', Math.round(file.size / 1024));

    const uploadResponse = await apiClient.makeRequest(`/api/v1/media/albums/${albumId}/media/upload`, {
      method: 'POST',
      data: formData,
      isFormData: true
    });

    const mediaFile = uploadResponse?.media_file || uploadResponse?.media || uploadResponse;
    const urlCandidate = mediaFile?.public_url || mediaFile?.url;
    const resolvedUrl = toAbsoluteHttpUrl(urlCandidate) || urlCandidate;
    if (!resolvedUrl) {
      throw new Error(t('imageGenerator.video.uploadsUploadError', { defaultValue: 'Upload fallito. Riprova.' }));
    }

    return {
      id: mediaFile?.id || mediaFile?._id || generateMediaId(),
      name: mediaFile?.name || file.name || t('desktop.upload.defaults.referenceName', { defaultValue: 'Riferimento' }),
      url: resolvedUrl,
      raw: mediaFile
    };
  }, [apiClient, ensureUploadsAlbumId, t]);

  const handleVideoFrameBrowse = useCallback((frameType) => {
    setVideoReferenceError('');
    const targetRef = frameType === 'first' ? videoFirstFrameInputRef : videoLastFrameInputRef;
    if (targetRef.current) {
      targetRef.current.click();
    }
  }, []);

  const handleVideoFrameFiles = useCallback(async (files, frameType) => {
    setVideoReferenceError('');
    if (!files || files.length === 0) {
      return;
    }

    const file = Array.from(files).filter(Boolean)[0];
    if (!file) return;

    const allowedTypes = new Set(['image/png', 'image/jpeg', 'image/jpg']);
    const mimeType = (file?.type || '').toLowerCase();
    const fileName = (file?.name || '').toLowerCase();
    const isAllowed = allowedTypes.has(mimeType) || /\.(png|jpe?g)$/.test(fileName);

    if (!isAllowed) {
      setVideoReferenceError(t('postReview.imageEditor.references.typeError', { defaultValue: 'Carica solo file PNG o JPG.' }));
      return;
    }

    if (isImageTooLarge(file)) {
      setVideoReferenceError(t('postReview.imageEditor.references.sizeError', { maxMB: 5, defaultValue: 'Limite massimo 5MB.' }));
      return;
    }

    setVideoReferenceUploading(true);
    try {
      const uploadedItem = await uploadReferenceImageToUploads(file);
      if (frameType === 'first') {
        setVideoFirstFrame(uploadedItem);
      } else {
        setVideoLastFrame(uploadedItem);
      }
    } catch (error) {
      console.error('[ImageGenerationPage] Video frame upload failed', error);
      setVideoReferenceError(error?.message || t('imageGenerator.video.references.uploadError', { defaultValue: 'Errore nel caricamento dei riferimenti.' }));
    } finally {
      setVideoReferenceUploading(false);
    }
  }, [isImageTooLarge, t, uploadReferenceImageToUploads]);

  const handleClearVideoFrame = useCallback((frameType) => {
    if (frameType === 'first') {
      setVideoFirstFrame(null);
    } else {
      setVideoLastFrame(null);
    }
  }, []);

  const triggerVideoFrameGallery = useCallback((frameType) => {
    const targetType = frameType === 'first' ? 'video-first-frame' : 'video-last-frame';
    lastGalleryTypeRef.current = targetType;
    setShowGallery(targetType);
  }, []);

  const handleAddVideoReferenceUrl = useCallback(() => {
    const trimmed = (videoReferenceVideoInput || '').trim();
    if (!trimmed) return;
    const normalized = toAbsoluteHttpUrl(trimmed);
    if (!normalized) {
      setVideoReferenceError(t('imageGenerator.video.references.videoUrlError', { defaultValue: 'Inserisci un URL valido.' }));
      return;
    }

    setVideoReferenceVideos((prev) => {
      if (prev.includes(normalized)) {
        return prev;
      }
      return [...prev, normalized];
    });
    setVideoReferenceVideoInput('');
    setVideoReferenceError('');
  }, [videoReferenceVideoInput, t]);

  const handleRemoveVideoReferenceUrl = useCallback((url) => {
    if (!url) return;
    setVideoReferenceVideos((prev) => prev.filter((entry) => entry !== url));
  }, []);

  const isVideoFile = useCallback((url) => {
    if (!url) return false;
    const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.ogg', '.mkv', '.m4v'];
    const value = url.toLowerCase();
    return videoExtensions.some((ext) => value.includes(ext));
  }, []);

  const generateVideoThumbnail = useCallback((videoUrl) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.playsInline = true;

      const handleLoadedData = () => {
        try {
          const aspectRatio = video.videoWidth / video.videoHeight;
          canvas.width = 120;
          canvas.height = 120 / aspectRatio;
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const thumbnailDataUrl = canvas.toDataURL('image/jpeg', 0.8);
          resolve(thumbnailDataUrl);
        } catch (error) {
          reject(error);
        } finally {
          video.removeEventListener('loadeddata', handleLoadedData);
          video.removeEventListener('error', handleError);
          video.src = '';
          video.load();
        }
      };

      const handleError = (error) => {
        video.removeEventListener('loadeddata', handleLoadedData);
        video.removeEventListener('error', handleError);
        reject(new Error('Failed to load video for thumbnail generation'));
      };

      video.addEventListener('loadeddata', handleLoadedData);
      video.addEventListener('error', handleError);

      video.src = videoUrl;
      video.currentTime = 0.1;
    });
  }, []);

  const manualFileInputRef = useRef(null);

  const manualRevokePreviewUrl = useCallback((mediaItem) => {
    if (!mediaItem || mediaItem.type !== 'file') {
      return;
    }

    const previewUrl = mediaItem.previewUrl;
    if (previewUrl && previewUrl.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(previewUrl);
      } catch (error) {
        console.warn('[ImageGenerationPage] Failed to revoke manual media URL', error);
      }
      manualMediaUrlsRef.current = manualMediaUrlsRef.current.filter((url) => url !== previewUrl);
    }
  }, []);

  const createManualMediaItemFromFile = useCallback((file) => {
    if (!(file instanceof File)) {
      return null;
    }

    const previewUrl = URL.createObjectURL(file);
    manualMediaUrlsRef.current.push(previewUrl);

    return {
      id: generateMediaId(),
      type: 'file',
      file,
      previewUrl,
      name: file.name || t('postReview.postEdit.media.image', { defaultValue: 'Immagine' }),
      fileType: file.type,
      fileSize: file.size
    };
  }, [t]);

  const createManualMediaItemFromUrl = useCallback((url, previewUrl, options = {}) => {
    if (!url) {
      return null;
    }

    const mediaFileId = options.mediaFileId ?? options.media_file_id ?? null;
    const name = options.name || null;

    return {
      id: generateMediaId(),
      type: 'url',
      url,
      previewUrl: previewUrl || url,
      mediaFileId: mediaFileId ? `${mediaFileId}` : null,
      name: name || undefined
    };
  }, []);

  const addManualMedia = useCallback((mediaItems) => {
    if (!mediaItems || mediaItems.length === 0) return;
    setPostValidation('');
    setManualMedia((prev = []) => [...prev, ...mediaItems.filter(Boolean)]);
  }, []);

  const handleManualFiles = useCallback((files) => {
    if (!files || files.length === 0) return;
    const mediaItems = [];
    let hasInvalidFile = false;
    let hasOversizeImage = false;

    Array.from(files).forEach((file) => {
      if (!(file instanceof File)) {
        hasInvalidFile = true;
        return;
      }

      if (isImageTooLarge(file)) {
        hasOversizeImage = true;
        return;
      }

      if (!isSupportedMediaFile(file)) {
        hasInvalidFile = true;
        console.warn('[ImageGenerationPage] Unsupported media format selected', {
          name: file?.name,
          type: file?.type
        });
        return;
      }

      const mediaItem = createManualMediaItemFromFile(file);
      if (mediaItem) {
        mediaItems.push(mediaItem);
      }
    });

    if (mediaItems.length > 0) {
      addManualMedia(mediaItems);
    }

    if (hasOversizeImage && notify) {
      notify({
        variant: 'error',
        title: imageTooLargeMessage
      });
    }

    if (hasInvalidFile && notify) {
      notify({
        variant: 'error',
        title: unsupportedFormatMessage
      });
    }
  }, [addManualMedia, createManualMediaItemFromFile, imageTooLargeMessage, notify, unsupportedFormatMessage]);

  const handleManualGallerySelect = useCallback((image) => {
    if (!image) {
      closeGallery();
      return;
    }

    const mediaItems = [];
    let hasOversizeImage = false;
    const pushFromSource = (item) => {
      if (!item) return;

      if (item.file instanceof File) {
        if (isImageTooLarge(item.file)) {
          hasOversizeImage = true;
          return;
        }
        mediaItems.push(createManualMediaItemFromFile(item.file));
        return;
      }

      const sourceUrl = item.selectedUrl || item.url || item.displayUrl || item.raw?.public_url || item.raw?.url || item.raw?.file_url;
      const preview = item.displayUrl || item.previewUrl || sourceUrl;
      const mediaFileId = resolveMediaFileId(item) || resolveMediaFileId(item.raw);
      const mediaItem = createManualMediaItemFromUrl(sourceUrl, preview, {
        mediaFileId,
        name: item.name || item.title || item.filename
      });
      if (mediaItem) {
        mediaItems.push(mediaItem);
      }
    };

    if (Array.isArray(image)) {
      image.forEach(pushFromSource);
    } else if (image?.files && Array.isArray(image.files)) {
      image.files.forEach((file) => {
        if (file instanceof File) {
          if (isImageTooLarge(file)) {
            hasOversizeImage = true;
            return;
          }
          mediaItems.push(createManualMediaItemFromFile(file));
        }
      });
    } else {
      pushFromSource(image);
    }

    const sanitizedItems = mediaItems.filter(Boolean);
    if (sanitizedItems.length > 0) {
      addManualMedia(sanitizedItems);
    }
    if (hasOversizeImage && notify) {
      notify({
        variant: 'error',
        title: imageTooLargeMessage
      });
    }
    closeGallery();
  }, [addManualMedia, closeGallery, createManualMediaItemFromFile, createManualMediaItemFromUrl, imageTooLargeMessage, notify, resolveMediaFileId]);

  const closeManualAIModal = useCallback(() => {
    setManualAIModalOpen(false);
    setManualAiModalType(null);
  }, []);

  const generateManualImageFromPrompt = useCallback(async (promptValue) => {
    const sanitizedPrompt = (promptValue || '').trim();
    if (!sanitizedPrompt) return false;

    setManualImageGenerating(true);
    try {
      if (onGenerateManualImageFromPrompt) {
        const result = await onGenerateManualImageFromPrompt(sanitizedPrompt);
        if (result?.url) {
          const mediaFileId = resolveMediaFileId(result);
          const mediaItem = createManualMediaItemFromUrl(result.url, result.url, { mediaFileId });
          if (mediaItem) {
            addManualMedia([{
              ...mediaItem,
              name: result.name || sanitizedPrompt || t('postReview.postEdit.media.image', { defaultValue: 'Immagine' })
            }]);
            return true;
          }
        }
      } else {
        const response = await apiClient.post('/api/v1/ai/generate-images', {
          prompt: sanitizedPrompt,
          count: 1,
          ratio: '4:5',
          size: mapRatioToSize('4:5')
        });
        const imageEntry = response?.images?.[0] || response?.results?.images?.[0];
        const url = imageEntry?.url || imageEntry?.local_url;
        if (url) {
          const mediaFileId = resolveMediaFileId(imageEntry);
          const mediaItem = createManualMediaItemFromUrl(url, url, { mediaFileId }) || {
            id: generateMediaId(),
            type: 'url',
            url,
            previewUrl: url,
            mediaFileId: mediaFileId ? `${mediaFileId}` : null
          };
          addManualMedia([{
            ...mediaItem,
            name: sanitizedPrompt || t('postReview.postEdit.media.image', { defaultValue: 'Immagine' })
          }]);
          return true;
        }
      }
    } catch (error) {
      console.error('[ImageGenerationPage] Error generating manual image from prompt', error);
      if (notify) {
        notify({ variant: 'error', title: error?.message || t('imageGenerator.status.error', { defaultValue: 'Errore nella generazione, riprova.' }) });
      }
    } finally {
      setManualImageGenerating(false);
    }
    return false;
  }, [addManualMedia, apiClient, createManualMediaItemFromUrl, notify, onGenerateManualImageFromPrompt, t, resolveMediaFileId]);

  const generateDescriptionFromPrompt = useCallback(async (promptValue) => {
    const effectivePrompt = (promptValue || postPrompt || '').trim();
    if (!effectivePrompt) {
      showPostAlert(t('imageGenerator.post.validation.prompt', { defaultValue: 'Aggiungi un prompt per il post.' }));
      return;
    }
    if (useStrategyForPost && !selectedCampaign) {
      showPostAlert(t('imageGenerator.post.validation.campaign', { defaultValue: 'Seleziona una campagna.' }));
      return;
    }

    setDescriptionLoading(true);
    try {
      if (onGenerateDescription) {
        const result = await onGenerateDescription({ prompt: effectivePrompt, campaignId: selectedCampaign?.id });
        if (result?.text) {
          setManualDescription(result.text);
        }
        closeManualAIModal();
        return;
      }

      const hasCampaign = Boolean(selectedCampaign);
      const campaignId = hasCampaign
        ? (selectedCampaign.id || selectedCampaign._id || selectedCampaign.value)
        : null;
      const shouldAttachCampaignId = Boolean(hasCampaign && (useStrategyForPost || useDnaForPost));

      const platforms = (() => {
        if (hasCampaign) {
          const resolved = extractPlatformsFromCampaign(selectedCampaign);
          if (resolved && resolved.length > 0) {
            return resolved.map((platform) => `${platform}`.toLowerCase());
          }
        }
        return ['instagram'];
      })();

      const apiPayload = {
        use_dna: Boolean(useDnaForPost),
        user_prompt: effectivePrompt,
        platform: platforms[0] || 'instagram'
      };

      if (shouldAttachCampaignId && campaignId) {
        apiPayload.campaign_id = campaignId;
      }

      const response = await apiClient.post('/api/v1/posts/generate-post-text', apiPayload);
      const generatedText = response?.content || response?.data?.content;
      const hashtags = normalizeHashtags(
        response?.metadata?.hashtags
        || response?.data?.metadata?.hashtags
        || response?.hashtags
      );

      if (!generatedText) {
        throw new Error(t('imageGenerator.post.validation.error', { defaultValue: 'Errore nella generazione del testo.' }));
      }

      const hashtagsText = hashtags.length ? `\n\n${hashtags.join(' ')}` : '';
      setManualDescription(`${generatedText}${hashtagsText}`);
      closeManualAIModal();
    } catch (error) {
      console.error('[ImageGenerationPage] Description generation failed', error);
      if (notify) {
        notify({ variant: 'error', title: error?.message || t('imageGenerator.post.validation.error', { defaultValue: 'Errore nella generazione del testo.' }) });
      }
    } finally {
      setDescriptionLoading(false);
    }
  }, [
    apiClient,
    closeManualAIModal,
    notify,
    onGenerateDescription,
    postPrompt,
    selectedCampaign,
    showPostAlert,
    t,
    useDnaForPost,
    useStrategyForPost
  ]);

  const handleManualPromptAdd = useCallback(() => {
    setManualAiModalType('image');
    setManualAIModalOpen(true);
  }, []);

  const handleManualAIModalRegenerate = useCallback(async (data) => {
    const promptValue = data?.prompt || '';
    const type = data?.type;

    if (type === 'text') {
      await generateDescriptionFromPrompt(promptValue);
      return;
    }

    if (type === 'image') {
      const generated = await generateManualImageFromPrompt(promptValue);
      if (generated) {
        closeManualAIModal();
      }
    }
  }, [closeManualAIModal, generateDescriptionFromPrompt, generateManualImageFromPrompt]);

  const handleManualRemove = useCallback((index) => {
    setManualMedia((prev) => {
      if (!prev || index < 0 || index >= prev.length) return prev;
      const itemToRemove = prev[index];
      manualRevokePreviewUrl(itemToRemove);
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
  }, [manualRevokePreviewUrl]);

  const handleManualPointerMove = useCallback((clientX, clientY, draggedIndex, force = false) => {
    if (!manualIsDraggingRef.current && !force) return;

    setManualDragCurrentPos({ x: clientX, y: clientY });

    const elementBelow = document.elementFromPoint(clientX, clientY);
    const imageItem = elementBelow?.closest('.post-edit-image-item');
    const deleteZone = elementBelow?.closest('[data-delete-zone="true"]');

    if (deleteZone) {
      manualIsOverTrashRef.current = true;
      manualDragOverIndexRef.current = null;
      setManualIsOverTrash(true);
      setManualDragOverIndex(null);
      return;
    }

    if (imageItem && !imageItem.classList.contains('post-edit-add-image-item')) {
      const targetIndex = parseInt(imageItem.dataset.index, 10);
      if (!Number.isNaN(targetIndex) && targetIndex !== draggedIndex) {
        manualDragOverIndexRef.current = targetIndex;
        manualIsOverTrashRef.current = false;
        setManualDragOverIndex(targetIndex);
        setManualIsOverTrash(false);
      }
      return;
    }

    manualIsOverTrashRef.current = false;
    manualDragOverIndexRef.current = null;
    setManualIsOverTrash(false);
    setManualDragOverIndex(null);
  }, []);

  const handleManualPointerEnd = useCallback((clientX, clientY, index) => {
    if (!manualIsDraggingRef.current) {
      return;
    }

    let finalTargetIndex = null;
    let droppedOnDelete = false;

    const elementBelow = document.elementFromPoint(clientX, clientY);
    const imageItem = elementBelow?.closest('.post-edit-image-item');
    const deleteZone = elementBelow?.closest('[data-delete-zone="true"]');

    if (deleteZone) {
      droppedOnDelete = true;
    } else if (imageItem && !imageItem.classList.contains('post-edit-add-image-item')) {
      const targetIndex = parseInt(imageItem.dataset.index, 10);
      if (!Number.isNaN(targetIndex) && targetIndex !== index) {
        finalTargetIndex = targetIndex;
      }
    }

    if (finalTargetIndex === null && !droppedOnDelete) {
      if (manualIsOverTrashRef.current) {
        droppedOnDelete = true;
      } else if (manualDragOverIndexRef.current !== null && manualDragOverIndexRef.current !== index) {
        finalTargetIndex = manualDragOverIndexRef.current;
      }
    }

    if (droppedOnDelete) {
      handleManualRemove(index);
    } else if (finalTargetIndex !== null && finalTargetIndex !== index) {
      setManualMedia((prev) => {
        if (!prev || index < 0 || index >= prev.length || finalTargetIndex < 0 || finalTargetIndex >= prev.length) {
          return prev;
        }
        const updated = [...prev];
        const [moved] = updated.splice(index, 1);
        updated.splice(finalTargetIndex, 0, moved);
        return updated;
      });
    }

    manualIsDraggingRef.current = false;
    manualIsOverTrashRef.current = false;
    manualDragOverIndexRef.current = null;

    setManualIsDragging(false);
    setManualIsOverTrash(false);
    setManualDragOverIndex(null);
    setManualDraggedIndex(null);
  }, [handleManualRemove]);

  const handleManualMouseDown = useCallback((event, index) => {
    if (event.target.closest('button')) {
      return;
    }
    event.preventDefault();

    const startX = event.clientX;
    const startY = event.clientY;
    const rect = event.currentTarget?.getBoundingClientRect?.() || { width: 120, height: 150 };
    setManualDragCurrentPos({ x: startX, y: startY });
    setManualDraggedImageDimensions({ width: rect.width, height: rect.height });
    manualDragStartRef.current = { x: startX, y: startY };
    manualIsDraggingRef.current = true;
    manualDragOverIndexRef.current = null;
    manualIsOverTrashRef.current = false;
    setManualIsDragging(true);
    setManualDraggedIndex(index);
    setManualDragOverIndex(null);
    setManualIsOverTrash(false);
    handleManualPointerMove(startX, startY, index, true);

    const handleMouseMove = (moveEvent) => {
      handleManualPointerMove(moveEvent.clientX, moveEvent.clientY, index);
    };

    const handleMouseUp = (upEvent) => {
      handleManualPointerEnd(upEvent.clientX, upEvent.clientY, index);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [handleManualPointerEnd, handleManualPointerMove]);

  const handleManualTouchStart = useCallback((event, index) => {
    if (event.target.closest('button')) {
      return;
    }
    event.preventDefault();
    const touch = event.touches[0];
    const startX = touch.clientX;
    const startY = touch.clientY;
    const rect = event.currentTarget?.getBoundingClientRect?.() || { width: 120, height: 150 };
    setManualDragCurrentPos({ x: startX, y: startY });
    setManualDraggedImageDimensions({ width: rect.width, height: rect.height });
    manualDragStartRef.current = { x: startX, y: startY };
    manualIsDraggingRef.current = true;
    manualDragOverIndexRef.current = null;
    manualIsOverTrashRef.current = false;
    setManualIsDragging(true);
    setManualDraggedIndex(index);
    setManualDragOverIndex(null);
    setManualIsOverTrash(false);
    handleManualPointerMove(startX, startY, index, true);

    const handleTouchMove = (moveEvent) => {
      if (!moveEvent.touches[0]) return;
      const current = moveEvent.touches[0];
      handleManualPointerMove(current.clientX, current.clientY, index);
    };

    const handleTouchEnd = (endEvent) => {
      const touchEnd = endEvent.changedTouches && endEvent.changedTouches[0];
      const endX = touchEnd ? touchEnd.clientX : manualDragStartRef.current.x;
      const endY = touchEnd ? touchEnd.clientY : manualDragStartRef.current.y;
      handleManualPointerEnd(endX, endY, index);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };

    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: false });
  }, [handleManualPointerEnd, handleManualPointerMove]);

  useEffect(() => {
    const generateThumbnails = async () => {
      if (!manualMedia || manualMedia.length === 0) return;

      for (let i = 0; i < manualMedia.length; i++) {
        const mediaItem = manualMedia[i];
        const displaySource = mediaItem?.previewUrl || mediaItem?.url;
        const thumbnailKey = `${mediaItem?.id || displaySource}-${i}`;
        const isVideo = mediaItem?.file
          ? !!mediaItem.file && mediaItem.file.type?.startsWith('video/')
          : isVideoFile(displaySource);

        if (isVideo && displaySource && !manualVideoThumbnails[thumbnailKey] && !manualThumbnailsLoading[thumbnailKey]) {
          setManualThumbnailsLoading((prev) => ({ ...prev, [thumbnailKey]: true }));

          try {
            const thumbnail = await generateVideoThumbnail(displaySource);
            setManualVideoThumbnails((prev) => ({ ...prev, [thumbnailKey]: thumbnail }));
          } catch (error) {
            console.error('[ImageGenerationPage] Error generating manual video thumbnail:', error);
          } finally {
            setManualThumbnailsLoading((prev) => ({ ...prev, [thumbnailKey]: false }));
          }
        }
      }
    };

    generateThumbnails();
  }, [manualMedia, isVideoFile, generateVideoThumbnail, manualVideoThumbnails, manualThumbnailsLoading]);

  const resolveCampaignPlatforms = useCallback((campaign) => {
    const platforms = extractPlatformsFromCampaign(campaign);
    if (platforms && platforms.length > 0) {
      return platforms.map((platform) => `${platform}`.toLowerCase());
    }
    return ['instagram'];
  }, []);

  const extractPostIdFromResponse = useCallback((response) => {
    const pickId = (source) => {
      if (!source) return null;
      if (typeof source === 'string' || typeof source === 'number') {
        return `${source}`;
      }
      return (
        source.postId ||
        source.post_id ||
        source.id ||
        source._id ||
        source.slug ||
        source.postSlug ||
        null
      );
    };

    if (!response) return null;

    const directId = pickId(response);
    if (directId) return directId;

    const nestedCandidates = [
      response.post,
      response.data?.post,
      response.result?.post,
      response.post_data,
      response.data?.createdPost,
      response.result?.createdPost
    ];

    for (const candidate of nestedCandidates) {
      const resolved = pickId(candidate);
      if (resolved) return resolved;
    }

    const containerCandidates = [response.data, response.result];
    for (const container of containerCandidates) {
      const resolved = pickId(container);
      if (resolved) return resolved;
    }

    const postArrays = [response.posts, response.data?.posts, response.result?.posts];
    for (const postList of postArrays) {
      if (Array.isArray(postList) && postList.length > 0) {
        const resolved = pickId(postList[0]);
        if (resolved) return resolved;
      }
    }

    return null;
  }, []);

  const navigateToPostReview = useCallback((postId, source) => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams();
    if (postId) {
      params.set('postId', postId);
      params.set('targetPostId', postId);
    }
    if (source) params.set('source', source);
    const query = params.toString();
    const url = query ? `/postreview?${query}` : '/postreview';
    window.location.assign(url);
  }, []);

  const handleGeneratePostAuto = useCallback(async () => {
    if (!selectedCampaign) {
      showPostAlert(t('imageGenerator.post.validation.campaign', { defaultValue: 'Seleziona una campagna.' }));
      return;
    }
    if (!postPrompt.trim()) {
      showPostAlert(t('imageGenerator.post.validation.prompt', { defaultValue: 'Aggiungi un prompt per il post.' }));
      return;
    }
    if (scheduleError) {
      showPostAlert(scheduleError);
      return;
    }
    if (!scheduledAt) {
      showPostAlert(t('imageGenerator.schedule.required', { defaultValue: 'Seleziona data e ora future' }));
      return;
    }

    const confirmed = await confirmAction(t('imageGenerator.post.auto.confirm', { defaultValue: 'Vuoi generare il post con queste impostazioni?' }));
    if (!confirmed) return;

    const payload = {
      campaignId: selectedCampaign.id || selectedCampaign._id || selectedCampaign.value,
      prompt: postPrompt,
      useStrategy: useStrategyForPost,
      strategyId: useStrategyForPost && selectedStrategy ? selectedStrategy.id : null,
      useDna: useDnaForPost,
      dnaId: null,
      scheduled_time: scheduledAt ? scheduledAt.toISOString() : null,
      platforms: resolveCampaignPlatforms(selectedCampaign)
    };

    setPostValidation('');
    setAutoPostLoading(true);
    try {
      if (onGeneratePostAuto) {
        const result = await onGeneratePostAuto(payload);
        const generatedPostId = extractPostIdFromResponse(result);
        if (generatedPostId) {
          navigateToPostReview(generatedPostId, 'auto');
        } else {
          showPostAlert(t('imageGenerator.post.validation.error', { defaultValue: 'Errore nella generazione del post.' }), 'error');
        }
        return;
      }

      const apiPayload = {
        use_campaign: Boolean(useStrategyForPost),
        campaign_id: payload.campaignId,
        use_dna: Boolean(useDnaForPost),
        user_prompt: postPrompt,
        platforms: resolveCampaignPlatforms(selectedCampaign),
        scheduled_time: scheduledAt.toISOString()
      };
      const response = await apiClient.post('/api/v1/posts/generate-via-prompt', apiPayload);
      const postId = extractPostIdFromResponse(response);
      if (postId) {
        navigateToPostReview(postId, 'auto');
      } else {
        showPostAlert(t('imageGenerator.post.validation.error', { defaultValue: 'Errore nella generazione del post.' }), 'error');
      }
    } catch (error) {
      console.error('[ImageGenerationPage] Auto post generation failed', error);
      showPostAlert(error?.message || t('imageGenerator.post.validation.error', { defaultValue: 'Errore nella generazione del post.' }), 'error');
    } finally {
      setAutoPostLoading(false);
    }
  }, [
    selectedCampaign,
    scheduleError,
    postPrompt,
    useStrategyForPost,
    selectedStrategy,
    useDnaForPost,
    scheduledAt,
    onGeneratePostAuto,
    resolveCampaignPlatforms,
    extractPostIdFromResponse,
    apiClient,
    navigateToPostReview,
    showPostAlert,
    confirmAction,
    t
  ]);

  const handleOpenDescriptionModal = useCallback(() => {
    setManualAiModalType('text');
    setManualAIModalOpen(true);
  }, []);

  const guessExtensionFromMime = useCallback((mimeType) => {
    if (!mimeType || typeof mimeType !== 'string') return 'jpg';
    const [, subtype = 'jpg'] = mimeType.split('/');
    const lowered = subtype.toLowerCase();
    if (lowered.includes('jpeg')) return 'jpg';
    if (lowered.includes('png')) return 'png';
    if (lowered.includes('webp')) return 'webp';
    if (lowered.includes('gif')) return 'gif';
    if (lowered.includes('mp4')) return 'mp4';
    if (lowered.includes('quicktime') || lowered.includes('mov')) return 'mov';
    if (lowered.includes('ogg')) return 'ogg';
    if (lowered.includes('webm')) return 'webm';
    return lowered.split('+')[0] || 'jpg';
  }, []);

  const buildMediaFilename = useCallback((mediaItem, index, mimeType) => {
    const baseName = (mediaItem?.name && typeof mediaItem.name === 'string' ? mediaItem.name.trim() : '') || `media-${index + 1}`;
    const extension = guessExtensionFromMime(mimeType);
    const normalizedBase = baseName.replace(/\.[^/.]+$/, '').replace(/\s+/g, '-');
    const fileName = `${normalizedBase}.${extension}`;
    return fileName;
  }, [guessExtensionFromMime]);

  const downloadMediaAsFile = useCallback(async (mediaItem, index) => {
    const sourceUrl = mediaItem?.url || mediaItem?.previewUrl;
    if (!sourceUrl || typeof fetch !== 'function') {
      throw new Error(t('imageGenerator.post.validation.mediaDownload', { defaultValue: 'Impossibile allegare i media selezionati. Riprova.' }));
    }

    try {
      const response = await fetch(sourceUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch media: ${response.status}`);
      }
      const blob = await response.blob();
      const filename = buildMediaFilename(mediaItem, index, blob.type);
      return new File([blob], filename, { type: blob.type || 'application/octet-stream' });
    } catch (error) {
      console.error('[ImageGenerationPage] Unable to convert media URL to file', error);
      throw new Error(t('imageGenerator.post.validation.mediaDownload', { defaultValue: 'Impossibile allegare i media selezionati. Riprova.' }));
    }
  }, [buildMediaFilename, t]);

  const normalizeMediaToFile = useCallback(async (mediaItem, index) => {
    if (!mediaItem) return null;

    if (mediaItem.file instanceof File) {
      return mediaItem.file;
    }

    if (mediaItem.file instanceof Blob) {
      const mimeType = mediaItem.file.type || 'application/octet-stream';
      const filename = buildMediaFilename(mediaItem, index, mimeType);
      return new File([mediaItem.file], filename, { type: mimeType });
    }

    if (mediaItem.url || mediaItem.previewUrl) {
      return downloadMediaAsFile(mediaItem, index);
    }

    return null;
  }, [buildMediaFilename, downloadMediaAsFile]);

  const prepareManualMediaPayload = useCallback(async (postData) => {
    const mediaFileIds = [];
    let mediaIndex = 0;
    const formData = new FormData();

    for (let i = 0; i < manualMedia.length; i++) {
      const mediaItem = manualMedia[i];
      const mediaFileId = resolveMediaFileId(mediaItem) || resolveMediaFileId(mediaItem?.raw);
      if (mediaFileId) {
        mediaFileIds.push(`${mediaFileId}`);
        continue;
      }

      const normalizedFile = await normalizeMediaToFile(mediaItem, mediaIndex);
      if (!normalizedFile) {
        continue;
      }

      const filename = buildMediaFilename(mediaItem, mediaIndex, normalizedFile.type);
      formData.append(`media[${mediaIndex}]`, normalizedFile, filename);
      mediaIndex += 1;
    }

    if (mediaFileIds.length > 0) {
      postData.media_file_ids = mediaFileIds;
      mediaFileIds.forEach((id, idx) => formData.append(`media_file_ids[${idx}]`, id));
    }

    if (mediaIndex === 0 && mediaFileIds.length === 0) {
      throw new Error(t('imageGenerator.post.validation.media', { defaultValue: 'Aggiungi almeno un\'immagine.' }));
    }

    formData.append('post_data', JSON.stringify(postData));

    return { formData, mediaFileIdsCount: mediaFileIds.length, mediaCount: mediaIndex };
  }, [buildMediaFilename, manualMedia, normalizeMediaToFile, resolveMediaFileId, t]);

  const handleCreatePostManual = useCallback(async () => {
    if (!selectedCampaign) {
      showPostAlert(t('imageGenerator.post.validation.campaign', { defaultValue: 'Seleziona una campagna.' }));
      return;
    }
    const descriptionText = (manualDescription || '').trim();
    if (!descriptionText) {
      showPostAlert(t('imageGenerator.post.validation.description', { defaultValue: 'Aggiungi una descrizione.' }));
      return;
    }
    if (manualMedia.length === 0) {
      showPostAlert(t('imageGenerator.post.validation.media', { defaultValue: 'Aggiungi almeno un\'immagine.' }));
      return;
    }
    if (scheduleError) {
      showPostAlert(scheduleError);
      return;
    }
    if (!scheduledAt) {
      showPostAlert(t('imageGenerator.schedule.required', { defaultValue: 'Seleziona data e ora future' }));
      return;
    }

    const confirmed = await confirmAction(t('imageGenerator.post.manual.confirm', { defaultValue: 'Vuoi creare il post con questi contenuti?' }));
    if (!confirmed) return;

    const campaignId = selectedCampaign.id || selectedCampaign._id || selectedCampaign.value;

    setPostValidation('');
    setManualLoading(true);
    try {
      const postData = {
        campaign_id: campaignId,
        content: descriptionText,
        platforms: resolveCampaignPlatforms(selectedCampaign),
        scheduled_time: scheduledAt.toISOString()
      };

      const { formData, mediaFileIdsCount, mediaCount } = await prepareManualMediaPayload(postData);

      const payload = {
        campaignId,
        images: manualMedia,
        description: descriptionText,
        scheduled_time: postData.scheduled_time,
        platforms: postData.platforms,
        media_file_ids: postData.media_file_ids || [],
        mediaFilesCount: mediaCount,
        mediaFileIdsCount,
        formData
      };

      if (onCreatePostManual) {
        const result = await onCreatePostManual(payload);
        const createdPostId = extractPostIdFromResponse(result);
        if (createdPostId) {
          navigateToPostReview(createdPostId, 'manual');
        } else {
          showPostAlert(t('imageGenerator.post.validation.error', { defaultValue: 'Errore nella creazione del post.' }), 'error');
        }
        return;
      }
      const response = await apiClient.makeRequest('/api/v1/posts/create', {
        method: 'POST',
        data: formData,
        isFormData: true
      });

      const postId = extractPostIdFromResponse(response);
      if (postId) {
        navigateToPostReview(postId, 'manual');
      } else {
        showPostAlert(t('imageGenerator.post.validation.error', { defaultValue: 'Errore nella creazione del post.' }), 'error');
      }
    } catch (error) {
      console.error('[ImageGenerationPage] Manual post creation failed', error);
      showPostAlert(error?.message || t('imageGenerator.post.validation.error', { defaultValue: 'Errore nella creazione del post.' }), 'error');
    } finally {
      setManualLoading(false);
    }
  }, [
    selectedCampaign,
    manualMedia,
    manualDescription,
    scheduleError,
    scheduledAt,
    onCreatePostManual,
    resolveCampaignPlatforms,
    prepareManualMediaPayload,
    apiClient,
    extractPostIdFromResponse,
    navigateToPostReview,
    showPostAlert,
    confirmAction,
    t
  ]);

  const handleCampaignSelect = useCallback((campaign) => {
    setSelectedCampaign(campaign);
    setPostValidation('');
    setImageValidation('');

    const derivedStrategyId = deriveStrategyIdFromCampaign(campaign);
    if (derivedStrategyId) {
      setSelectedStrategyId(`${derivedStrategyId}`);
    } else {
      setSelectedStrategyId(null);
    }
  }, []);

  const renderImageReference = () => (
    <div className="prompt-editor-section">
      <h3 className="prompt-editor-section-title">
        {t('imageGenerator.reference.title', { defaultValue: 'Immagini di riferimento' })}
        <span className="prompt-editor-section-badge">{`${promptImages.length}/${MAX_REFERENCE_IMAGES}`}</span>
      </h3>
      <p className="prompt-editor-section-description">
        {t('imageGenerator.reference.description', { defaultValue: 'Aggiungi fino a 3 riferimenti opzionali per guidare la generazione.' })}
      </p>

      {promptImages.length > 0 ? (
        <div className="prompt-editor-images-list">
          {promptImages.map((img) => (
            <div className="prompt-editor-image-item" key={img.id}>
              <div className="prompt-editor-image-preview">
                <img
                  src={img.url}
                  alt={img.name}
                  onError={(e) => {
                    e.target.src = placeholderImage;
                  }}
                />
              </div>
              <div className="prompt-editor-image-info">
                <div className="prompt-editor-image-name">{img.name}</div>
              </div>
              <button
                className="prompt-editor-image-delete"
                onClick={() => handleDeletePromptImage(img.id)}
                aria-label={t('imageGenerator.reference.aria.delete', { defaultValue: 'Rimuovi riferimento' })}
              >
                <img src={iconDelete} alt={t('imageGenerator.reference.alt.delete', { defaultValue: 'Elimina' })} />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="prompt-editor-images-empty">
            <div className="prompt-editor-images-empty-icon">
              <img src={iconImage} alt={t('imageGenerator.reference.alt.empty', { defaultValue: 'Nessuna immagine' })} />
            </div>
            <p>{t('imageGenerator.reference.empty', { defaultValue: 'Nessuna immagine caricata' })}</p>
          </div>
        </>
      )}
      <div className="prompt-editor-upload-actions">
        <label className={`prompt-editor-upload-button ${promptImages.length >= MAX_REFERENCE_IMAGES ? 'disabled' : ''}`}>
          <input
            type="file"
            accept="image/*"
            onChange={handlePromptImageInputChange}
            hidden
            disabled={promptImages.length >= MAX_REFERENCE_IMAGES}
          />
          <img src={iconUpload} alt={t('imageGenerator.reference.alt.upload', { defaultValue: 'Carica' })} className="button-icon" />
          {t('imageGenerator.reference.actions.upload', { defaultValue: 'Carica' })}
        </label>
        <button
          type="button"
          className="prompt-editor-upload-button secondary"
          onClick={() => openGallery('reference')}
          disabled={promptImages.length >= MAX_REFERENCE_IMAGES}
        >
          <img src={iconImage} alt={t('imageGenerator.reference.alt.gallery', { defaultValue: 'Galleria' })} className="button-icon" />
          {t('imageGenerator.reference.actions.gallery', { defaultValue: 'Galleria' })}
        </button>
      </div>
      {promptImages.length >= MAX_REFERENCE_IMAGES && (
        <p className="prompt-editor-section-description">
          {t('imageGenerator.reference.limit', { defaultValue: 'Hai raggiunto il limite di 3 immagini di riferimento.' })}
        </p>
      )}
    </div>
  );

  const renderImageTab = () => {
    const currentImageUrl = generatedImage?.url ?? null;
    const isBaseImageToggleDisabled = imageMode === 'pro' || !currentImageUrl;
    const imageModeLabels = { simple: 'Fast', pro: 'Pro' };

    return (
      <>
        <div className="prompt-editor-current-image-container">
          {currentImageUrl ? (
            <img
              src={currentImageUrl}
              alt={generatedImage?.name || t('imageGenerator.alt.generatedImage', { defaultValue: 'Immagine generata' })}
              className="prompt-editor-current-image"
              onError={(e) => {
                e.target.src = placeholderImage;
              }}
            />
          ) : (
            <div className="prompt-editor-current-image placeholder">
              <div className="prompt-editor-placeholder-content">
                <img
                  src={iconImage}
                  alt={t('imageGenerator.reference.alt.empty', { defaultValue: 'Nessuna immagine' })}
                  className="prompt-editor-placeholder-icon"
                />
                <span className="prompt-editor-placeholder-text">
                  {t('imageGenerator.header.subtitle', { defaultValue: "Utilizza Loopino per dare una spinta alla tua creativita nella creazione di post o immagini" })}
                </span>
              </div>
            </div>
          )}
          {(imageLoading || isLoading) && (
            <AILoadingOverlay text={t('imageGenerator.status.generating', { defaultValue: 'Generazione in corso' })} />
          )}
        </div>

        {currentImageUrl && (
          <div
            className={`base-image-toggle-card ${isBaseImageToggleDisabled ? 'disabled' : ''} ${useGeneratedImageAsBase ? 'active' : ''}`}
            role="group"
            aria-label={t('imageGenerator.baseImage.aria', { defaultValue: 'Usa come base' })}
          >
            <div className="base-image-toggle-info">
              <span className="base-image-toggle-title">{t('imageGenerator.baseImage.title', { defaultValue: 'Usa come base' })}</span>
              <p className="base-image-toggle-description">
                {t('imageGenerator.baseImage.description', { defaultValue: 'Rigenera partendo da questa immagine.' })}
              </p>
            </div>
            <button
              type="button"
              className={`base-image-toggle-button ${useGeneratedImageAsBase ? 'active' : ''}`}
              onClick={() => {
                if (isBaseImageToggleDisabled || !generatedImage) return;
                setUseGeneratedImageAsBase((prev) => !prev);
              }}
              aria-pressed={useGeneratedImageAsBase}
              disabled={isBaseImageToggleDisabled || !generatedImage}
            >
              <span className="base-image-toggle-thumb" />
            </button>
          </div>
        )}

        <div className="prompt-editor-mode-toggle-container prompt-editor-image-mode-toggle">
          <div className="prompt-editor-mode-toggle">
            <button
              className={`prompt-editor-mode-toggle-option ${imageMode === 'simple' ? 'active' : ''}`}
              onClick={() => setImageMode('simple')}
            >
              {imageModeLabels.simple}
            </button>
            <button
              className={`prompt-editor-mode-toggle-option ${imageMode === 'pro' ? 'active' : ''}`}
              onClick={() => setImageMode('pro')}
            >
              {imageModeLabels.pro}
            </button>
          </div>
        </div>

        {imageMode === 'simple' && (
          <div className="prompt-editor-simple-mode">
            <div className="prompt-editor-section">
              <h3 className="prompt-editor-section-title">{t('imageGenerator.simple.promptTitle', { defaultValue: 'Prompt' })}</h3>
              <textarea
                className="prompt-editor-simple-textarea"
                value={simplePromptText}
                onChange={(e) => setSimplePromptText(e.target.value)}
                placeholder={t('imageGenerator.simple.promptPlaceholder', { defaultValue: 'Descrivi cosa vuoi generare' })}
                rows={6}
              />
            </div>
            {renderImageReference()}
          </div>
        )}

        {imageMode === 'pro' && (
          <div className="prompt-editor-pro-mode">
            <div className="prompt-editor-blocks">
              {blocks.map((block, idx) => {
                const match = /^\s*([^:]{1,40}):\s*(.*)$/.exec(block || '');
                const fallbackLabel = t('imageGenerator.pro.blockFallback', { index: idx + 1, defaultValue: `Blocco ${idx + 1}` });
                const label = match ? match[1].trim() : fallbackLabel;
                const content = match ? match[2] : (block || '');
                const placeholder = buildBlockPlaceholder(label, idx);
                return (
                  <div className="prompt-block-card" key={`block-${idx}`}>
                    <div className="prompt-block-header">
                      <div className="prompt-block-index">{idx + 1}</div>
                      <div className="prompt-block-label">{label}</div>
                    </div>
                    <textarea
                      className="prompt-block-textarea"
                      value={content}
                      onChange={(e) => handleBlockChange(idx, `${label}: ${e.target.value}`)}
                      placeholder={placeholder}
                      rows={3}
                    />
                  </div>
                );
              })}
            </div>

            <div className="prompt-editor-options">
              <div className="option-card">
                <h3 className="option-card-title">{t('imageGenerator.pro.options.title', { defaultValue: 'Formato' })}</h3>
                <div className="segmented-control">
                  {aspectRatioOptions.map(({ key, label }) => (
                    <button
                      key={key}
                      className={`segmented-option ${aspectRatio === key ? 'active' : ''}`}
                      onClick={() => setAspectRatio(key)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {aspectRatio === 'custom' && (
                  <div className="custom-dimensions">
                    <input
                      type="number"
                      className="dimension-input"
                      placeholder={t('imageGenerator.pro.options.width', { defaultValue: 'Larghezza' })}
                      value={customWidth}
                      onChange={(e) => setCustomWidth(e.target.value)}
                      min="256"
                      max="2048"
                    />
                    <span className="dimension-separator">×</span>
                    <input
                      type="number"
                      className="dimension-input"
                      placeholder={t('imageGenerator.pro.options.height', { defaultValue: 'Altezza' })}
                      value={customHeight}
                      onChange={(e) => setCustomHeight(e.target.value)}
                      min="256"
                      max="2048"
                    />
                  </div>
                )}
              </div>
            </div>

            {renderImageReference()}
          </div>
        )}

        <div className="prompt-editor-footer">
          {imageMode === 'pro' && (
            <button
              type="button"
              className="prompt-editor-regenerate-text"
              onClick={() => setShowPromptBlocksModal(true)}
              disabled={isRegeneratingBlocks || imageLoading || isLoading}
            >
              {isRegeneratingBlocks ? (
                <>
                  <div className="prompt-editor-spinner" />
                  {t('postReview.imageEditor.footer.regenerateBlocksLoading', { defaultValue: 'Rigenerazione in corso...' })}
                </>
              ) : (
                <>
                  <span className="button-icon">
                    <AIIcon size={18} />
                  </span>
                  {t('postReview.imageEditor.footer.regenerateBlocks', { defaultValue: 'Rigenera blocchi' })}
                </>
              )}
            </button>
          )}
          <button className="prompt-editor-generate-image" onClick={handleImageGenerate} disabled={imageLoading || isLoading}>
            {imageLoading || isLoading ? (
              <>
                <div className="prompt-editor-spinner" />
                {t('imageGenerator.status.generating', { defaultValue: 'Generazione in corso' })}
              </>
            ) : (
              <>
                <img src={iconImage} alt={t('imageGenerator.reference.alt.image', { defaultValue: 'Immagine' })} className="button-icon" />
                {t('imageGenerator.actions.generate', { defaultValue: 'Genera immagine' })}
              </>
            )}
          </button>
        </div>
      </>
    );
  };

  const renderVideoTab = () => {
    const currentVideoUrl = generatedVideo?.url ?? null;
    const isVideoJobActive = videoStatus === 'queued' || videoStatus === 'running';
    const isVideoBusy = videoLoading || videoReferenceUploading || videoModelsLoading;
    const videoProgressPercent = typeof videoProgress === 'number' ? Math.round(videoProgress * 100) : null;
    const videoLoadingLabel = videoProgressPercent !== null
      ? `${t('imageGenerator.video.footer.generating', { defaultValue: 'Generazione...' })} ${videoProgressPercent}%`
      : t('imageGenerator.video.footer.generating', { defaultValue: 'Generazione...' });

    const videoFrameSlots = [
      {
        key: 'first',
        label: t('imageGenerator.video.frames.first', { defaultValue: 'Primo frame (opzionale)' }),
        frame: videoFirstFrame
      },
      {
        key: 'last',
        label: t('imageGenerator.video.frames.last', { defaultValue: 'Ultimo frame (opzionale)' }),
        frame: videoLastFrame
      }
    ];

    const renderVideoFrameCard = ({ key, label, frame }) => {
      const hasFrame = Boolean(frame?.url);
      const subLabel = hasFrame
        ? (frame?.name || t('imageGenerator.video.frames.selected', { defaultValue: 'Frame selezionato' }))
        : t('imageGenerator.video.frames.optional', { defaultValue: 'Opzionale' });

      return (
        <div className="prompt-editor-video-frame-card" key={key}>
          <div className="prompt-editor-video-frame-preview">
            {hasFrame ? (
              <img
                src={frame.url}
                alt={frame.name || label}
                onError={(e) => {
                  e.target.src = placeholderImage;
                }}
              />
            ) : (
              <div className="prompt-editor-video-frame-placeholder">
                <img src={iconImage} alt="" aria-hidden="true" />
                <p>{t('imageGenerator.video.frames.empty', { defaultValue: 'Nessun frame selezionato' })}</p>
              </div>
            )}
          </div>
          <div className="prompt-editor-video-frame-body">
            <div className="prompt-editor-video-frame-text">
              <div className="prompt-editor-video-frame-title">{label}</div>
              <div className="prompt-editor-video-frame-sub">{subLabel}</div>
            </div>
            <div className="prompt-editor-video-frame-actions">
              <input
                ref={key === 'first' ? videoFirstFrameInputRef : videoLastFrameInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg"
                hidden
                onChange={(event) => {
                  handleVideoFrameFiles(event.target.files, key);
                  event.target.value = '';
                }}
              />
              <button
                type="button"
                className="prompt-editor-upload-button compact"
                onClick={() => handleVideoFrameBrowse(key)}
                disabled={videoReferenceUploading || videoLoading}
              >
                <img src={iconUpload} alt="" aria-hidden="true" className="button-icon" />
                {t('postReview.imageEditor.references.upload', { defaultValue: 'Carica' })}
              </button>
              <button
                type="button"
                className="prompt-editor-upload-button secondary compact"
                onClick={() => triggerVideoFrameGallery(key)}
                disabled={videoReferenceUploading || videoLoading}
              >
                <img src={iconImage} alt="" aria-hidden="true" className="button-icon" />
                {t('postReview.imageEditor.references.gallery', { defaultValue: 'Galleria' })}
              </button>
              {hasFrame && (
                <button
                  className="prompt-editor-image-delete compact"
                  onClick={() => handleClearVideoFrame(key)}
                  aria-label={t('postReview.imageEditor.references.deleteAria', { defaultValue: 'Rimuovi' })}
                  type="button"
                  disabled={videoReferenceUploading || videoLoading}
                >
                  <img src={iconDelete} alt={t('postReview.imageEditor.references.deleteAlt', { defaultValue: 'Elimina' })} />
                </button>
              )}
            </div>
          </div>
        </div>
      );
    };

    return (
      <>
        <div className="prompt-editor-current-image-container prompt-editor-current-video-container">
          {currentVideoUrl ? (
            <video
              src={currentVideoUrl}
              className="prompt-editor-current-video"
              controls
              preload="metadata"
            />
          ) : (
            <div className="prompt-editor-current-image placeholder">
              <div className="prompt-editor-placeholder-content">
                <img
                  src={iconVideo}
                  alt={t('imageGenerator.video.references.emptyAlt', { defaultValue: 'Video' })}
                  className="prompt-editor-placeholder-icon"
                />
                <span className="prompt-editor-placeholder-text">
                  {t('imageGenerator.video.currentVideoPlaceholder', { defaultValue: 'Genera il video' })}
                </span>
              </div>
            </div>
          )}
          {videoLoading && (
            <AILoadingOverlay text={t('imageGenerator.video.overlay.generating', { defaultValue: 'Generazione in corso' })} />
          )}
        </div>

        <div className="prompt-editor-section">
          <h3 className="prompt-editor-section-title">{t('postReview.imageEditor.fast.title', { defaultValue: 'Fast' })}</h3>
          <p className="prompt-editor-section-description">
            {t('imageGenerator.video.fast.subtitle', { defaultValue: 'Genera in pochi click usando prompt e riferimenti.' })}
          </p>
          <textarea
            className="prompt-editor-simple-textarea"
            value={videoSimplePromptText}
            onChange={(e) => setVideoSimplePromptText(e.target.value)}
            placeholder={t('imageGenerator.video.fast.placeholder', { defaultValue: 'Es: Una skyline futuristica al tramonto con droni in volo...' })}
            rows={6}
          />
        </div>

        <div className={`prompt-editor-section ${videoDropdownOpen === 'model' ? 'dropdown-open' : ''}`}>
          <h3 className="prompt-editor-section-title">{t('imageGenerator.video.settings.model', { defaultValue: 'Modello' })}</h3>
          <DropdownSelect
            value={selectedVideoModel}
            options={videoModelOptions}
            onChange={setSelectedVideoModel}
            disabled={videoModelsLoading || videoLoading}
            placeholder={t('imageGenerator.video.settings.modelAuto', { defaultValue: 'Auto' })}
            ariaLabel={t('imageGenerator.video.settings.model', { defaultValue: 'Modello' })}
            isOpen={videoDropdownOpen === 'model'}
            onOpenChange={(isOpen) => setVideoDropdownOpen(isOpen ? 'model' : null)}
          />
          {videoModelsLoading && (
            <p className="prompt-editor-section-description">
              {t('imageGenerator.video.models.loading', { defaultValue: 'Caricamento modelli...' })}
            </p>
          )}
          {videoModelsError && <div className="prompt-editor-validation">{videoModelsError}</div>}
        </div>

        <div className="prompt-editor-options prompt-editor-video-settings">
          <div className="option-card">
            <h3 className="option-card-title">{t('imageGenerator.video.settings.resolution', { defaultValue: 'Risoluzione' })}</h3>
            <div className="segmented-control">
              {VIDEO_RESOLUTION_OPTIONS.map((resolution) => {
                const isDisabled = isResolutionOptionDisabled(resolution);
                return (
                  <button
                    key={resolution}
                    className={`segmented-option ${videoResolution === resolution ? 'active' : ''}`}
                    onClick={() => setVideoResolution(resolution)}
                    type="button"
                    disabled={videoLoading || isDisabled}
                  >
                    {resolution}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="option-card">
            <h3 className="option-card-title">{t('imageGenerator.video.settings.fps', { defaultValue: 'FPS' })}</h3>
            <div className="segmented-control">
              {VIDEO_FPS_OPTIONS.map((option) => {
                const isDisabled = isFpsOptionDisabled(option.value);
                return (
                  <button
                    key={option.value}
                    className={`segmented-option ${normalizeVideoFpsValue(videoFps) === option.value ? 'active' : ''}`}
                    onClick={() => setVideoFps(option.value)}
                    type="button"
                    disabled={videoLoading || isDisabled}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className={`option-card ${videoDropdownOpen === 'duration' ? 'dropdown-open' : ''}`}>
            <h3 className="option-card-title">{t('imageGenerator.video.settings.duration', { defaultValue: 'Durata (s)' })}</h3>
            <DropdownSelect
              value={videoDuration}
              options={videoDurationOptions}
              onChange={setVideoDuration}
              disabled={videoLoading}
              ariaLabel={t('imageGenerator.video.settings.duration', { defaultValue: 'Durata (s)' })}
              isOpen={videoDropdownOpen === 'duration'}
              onOpenChange={(isOpen) => setVideoDropdownOpen(isOpen ? 'duration' : null)}
            />
          </div>

          <div className={`option-card ${videoDropdownOpen === 'count' ? 'dropdown-open' : ''}`}>
            <h3 className="option-card-title">{t('imageGenerator.video.settings.count', { defaultValue: 'Quantità' })}</h3>
            <DropdownSelect
              value={videoCount}
              options={videoCountOptions}
              onChange={setVideoCount}
              disabled={videoLoading}
              ariaLabel={t('imageGenerator.video.settings.count', { defaultValue: 'Quantità' })}
              isOpen={videoDropdownOpen === 'count'}
              onOpenChange={(isOpen) => setVideoDropdownOpen(isOpen ? 'count' : null)}
            />
          </div>

          <div className="option-card">
            <h3 className="option-card-title">{t('imageGenerator.video.settings.ratioTitle', { defaultValue: 'Aspect ratio' })}</h3>
            <div className="segmented-control">
              {VIDEO_RATIO_OPTIONS.map((ratio) => (
                <button
                  key={ratio}
                  className={`segmented-option ${videoAspectRatio === ratio ? 'active' : ''}`}
                  onClick={() => setVideoAspectRatio(ratio)}
                  type="button"
                  disabled={videoLoading}
                >
                  {ratio}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="prompt-editor-section">
          <h3 className="prompt-editor-section-title">
            {t('imageGenerator.video.references.eyebrow', { defaultValue: 'Media di riferimento' })}
          </h3>
          <p className="prompt-editor-section-description">
            {t('imageGenerator.video.references.description', { defaultValue: "Aggiungi immagini guida per l'AI" })}
          </p>

          <div className="prompt-editor-video-frame-grid">
            {videoFrameSlots.map((slot) => renderVideoFrameCard(slot))}
          </div>

          {videoReferenceUploading && (
            <div className="prompt-editor-upload-status">
              <div className="prompt-editor-spinner" />
              <span>{t('imageGenerator.video.references.uploading', { defaultValue: 'Caricamento in corso...' })}</span>
            </div>
          )}

          {videoReferenceError && <div className="prompt-editor-validation">{videoReferenceError}</div>}

          <div className="prompt-editor-video-url">
            <label className="prompt-editor-section-title prompt-editor-video-url-label">
              {t('imageGenerator.video.references.videoLabel', { defaultValue: 'Video di riferimento (URL)' })}
            </label>
            <div className="prompt-editor-video-url-row">
              <input
                className="prompt-editor-input"
                value={videoReferenceVideoInput}
                onChange={(e) => setVideoReferenceVideoInput(e.target.value)}
                placeholder={t('imageGenerator.video.references.videoDisabledPlaceholder', { defaultValue: 'Non disponibile con il modello corrente.' })}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleAddVideoReferenceUrl();
                  }
                }}
                disabled
              />
              <button
                type="button"
                className="prompt-editor-upload-button secondary compact"
                onClick={handleAddVideoReferenceUrl}
                disabled
              >
                {t('imageGenerator.video.references.addVideo', { defaultValue: 'Aggiungi' })}
              </button>
            </div>
            {videoReferenceVideos.length > 0 ? (
              <div className="prompt-editor-images-list">
                {videoReferenceVideos.map((url) => (
                  <div className="prompt-editor-image-item" key={url}>
                    <div className="prompt-editor-image-preview">
                      <img src={iconVideo} alt="" className="prompt-editor-video-ref-icon" />
                    </div>
                    <div className="prompt-editor-image-info">
                      <div className="prompt-editor-image-name">
                        {t('imageGenerator.video.references.videoItem', { defaultValue: 'Video riferimento' })}
                      </div>
                      <div className="prompt-editor-image-meta">{url}</div>
                    </div>
                    <button
                      className="prompt-editor-image-delete"
                      onClick={() => handleRemoveVideoReferenceUrl(url)}
                      aria-label={t('postReview.imageEditor.references.deleteAria', { defaultValue: 'Rimuovi' })}
                      type="button"
                    >
                      <img src={iconDelete} alt={t('postReview.imageEditor.references.deleteAlt', { defaultValue: 'Elimina' })} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="prompt-editor-section-description">
                {t('imageGenerator.video.references.videoDisabledHelper', { defaultValue: 'Non disponibile con il modello corrente (Veo 3.1).' })}
              </p>
            )}
          </div>
        </div>

        <div className="prompt-editor-footer">
          {videoLoading ? (
            <div className="prompt-editor-loading-actions">
              <div className="prompt-editor-loading-status">
                <div className="prompt-editor-spinner" />
                <span>{videoLoadingLabel}</span>
              </div>
              {isVideoJobActive && (
                <button
                  type="button"
                  className="prompt-editor-cancel-button"
                  onClick={handleCancelVideoJob}
                >
                  {t('imageGenerator.video.footer.cancel', { defaultValue: 'Annulla' })}
                </button>
              )}
            </div>
          ) : (
            <button
              className="prompt-editor-generate-image"
              onClick={handleGenerateVideo}
              disabled={isVideoBusy || isLoading}
              type="button"
            >
              <img src={iconVideo} alt={t('imageGenerator.video.footer.iconAlt', { defaultValue: 'Video' })} className="button-icon" />
              {t('imageGenerator.video.footer.generate', { defaultValue: 'Genera video' })}
            </button>
          )}
        </div>

        {videoError && <div className="prompt-editor-validation">{videoError}</div>}
      </>
    );
  };

  const renderPostContextSelectors = (mode = 'auto') => (
    <>
      {mode === 'auto' && (
        <div className="generation-settings-card">
          <div className="generation-settings-header">
            <h3>{t('imageGenerator.post.context.title', { defaultValue: 'Contesto post' })}</h3>
            <p>{t('imageGenerator.post.context.subtitle', { defaultValue: 'Attiva o disattiva il DNA aziendale.' })}</p>
          </div>
          <div className="generation-settings-toggle">
            <div className="generation-toggle-row">
              <div className="generation-toggle-title">
                {t('imageGenerator.post.auto.useDnaTitle', { defaultValue: 'Usa DNA per la generazione' })}
              </div>
              <DualOptionToggle
                active={useDnaForPost}
                onChange={(next) => {
                  dnaToggleTouchedRef.current = true;
                  setUseDnaForPost(next);
                }}
                leftLabel={t('imageGenerator.toggle.off', { defaultValue: 'Off' })}
                rightLabel={t('imageGenerator.toggle.on', { defaultValue: 'On' })}
                disabled={!selectedDna && availableDnaItems.length === 0}
                ariaLabel={t('imageGenerator.post.auto.dnaToggleAria', { defaultValue: 'Attiva o disattiva l\'uso del DNA per il post' })}
              />
            </div>
          </div>
        </div>
      )}

        <div className="generation-settings-card">
          <div className="generation-settings-header">
            <h3>{t('imageGenerator.post.auto.campaignTitle', { defaultValue: 'Campagna' })}</h3>
            <p>{t('imageGenerator.post.auto.campaignSubtitle', { defaultValue: 'è necessario scegliere la campagna in cui posizionare il tuo post' })}</p>
          </div>
          <MobileCampaignSelector
            mode="inline"
            selectionMode="single"
            activeCampaign={selectedCampaign}
            onCampaignSelect={handleCampaignSelect}
            campaigns={campaignOptions}
            requireConfirmation={false}
            showAllOption={false}
          />
        {mode === 'auto' && (
          <div className="generation-settings-toggle">
            <div className="generation-toggle-row">
              <div className="generation-toggle-title">
                {t('imageGenerator.post.auto.useStrategyTitle', { defaultValue: 'Usa strategia per la generazione' })}
              </div>
              <DualOptionToggle
                active={useStrategyForPost}
                onChange={(next) => setUseStrategyForPost(next)}
                leftLabel={t('imageGenerator.toggle.off', { defaultValue: 'Off' })}
                rightLabel={t('imageGenerator.toggle.on', { defaultValue: 'On' })}
                ariaLabel={t('imageGenerator.post.auto.strategyToggleAria', { defaultValue: 'Attiva o disattiva la strategia di campagna' })}
              />
            </div>
          </div>
        )}
      </div>

      <div className="generation-settings-card">
        <div className="generation-settings-header">
          <h3>{t('postReview.postEdit.time.title')}</h3>
          <p>{t('postReview.postEdit.time.subtitle')}</p>
        </div>

        <div className="post-edit-datetime-container mobile-datetime-container">
          <div className="mobile-datetime-field date-picker-field" ref={dateFieldRef}>
            <label htmlFor="mobile-datetime" className="mobile-datetime-label">
              <CalendarIcon size={18} />
              <span>{t('postReview.postEdit.time.label')}</span>
            </label>

            <div className="date-picker-display-group">
              <input
                id="mobile-datetime"
                type="text"
                className="date-picker-input-display"
                value={displayValue}
                readOnly
                onFocus={() => !isSchedulingBusy && setIsDatePickerOpen(true)}
                onClick={() => !isSchedulingBusy && setIsDatePickerOpen(true)}
                aria-expanded={isDatePickerOpen}
                aria-controls="mobile-date-picker-popover"
                disabled={isSchedulingBusy}
              />
              <button
                type="button"
                className="date-picker-icon-btn"
                onClick={() => !isSchedulingBusy && setIsDatePickerOpen((prev) => !prev)}
                aria-label={t('postReview.postEdit.time.label')}
                aria-expanded={isDatePickerOpen}
                aria-controls="mobile-date-picker-popover"
                disabled={isSchedulingBusy}
              >
                <CalendarIcon size={16} />
              </button>
            </div>

          </div>
        </div>
        {scheduleError && (
          <div className="prompt-editor-validation">
            {scheduleError}
          </div>
        )}
      </div>
    </>
  );

  const renderManualMediaGrid = () => (
    <div className="post-edit-section">
      <h2 className="post-edit-section-title">{t('postReview.postEdit.images.title', { defaultValue: 'Immagini' })}</h2>
      <p className="post-edit-section-description">
        {t('postReview.postEdit.images.helper', { defaultValue: 'Aggiungi, riordina o rimuovi i media.' })}
      </p>

      <div className="post-edit-images-grid">
        {manualMedia.map((media, index) => {
          const displaySource = media?.previewUrl || media?.url || placeholderImage;
          const thumbnailKey = `${media?.id || displaySource}-${index}`;
          const isVideo = media?.file
            ? !!media.file && media.file.type?.startsWith('video/')
            : isVideoFile(media?.url || media?.previewUrl);
          const isLoading = manualThumbnailsLoading[thumbnailKey];
          const thumbnail = manualVideoThumbnails[thumbnailKey];

          const renderMediaContent = () => {
            if (isVideo && isLoading) {
              return (
                <div className="post-edit-image-loading">
                  <div className="post-edit-thumbnail-spinner"></div>
                  <span className="post-edit-loading-text">{t('postReview.postEdit.media.generatingThumbnail')}</span>
                </div>
              );
            }

            if (isVideo && thumbnail) {
              return (
                <>
                  <img
                    src={thumbnail}
                    alt={`Video thumbnail ${index + 1}`}
                    className="post-edit-image"
                    loading="lazy"
                    decoding="async"
                    onError={(e) => { e.target.src = placeholderImage; }}
                  />
                  <div className="post-edit-video-indicator">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <polygon points="5,3 19,12 5,21" fill="white"/>
                    </svg>
                  </div>
                </>
              );
            }

            if (isVideo) {
              return (
                <div className="post-edit-image-loading">
                  <div className="post-edit-video-placeholder">🎥</div>
                  <span className="post-edit-loading-text">{t('postReview.postEdit.media.videoLabel')}</span>
                </div>
              );
            }

            return (
              <img
                src={displaySource}
                alt={t('postReview.postEdit.media.imageAlt', { index: index + 1 })}
                className="post-edit-image"
                loading="lazy"
                decoding="async"
                onError={(e) => { e.target.src = placeholderImage; }}
              />
            );
          };

          return (
            <div
              key={media.id || index}
              className={`post-edit-image-item ${manualDraggedIndex === index && manualIsDragging ? 'dragging' : ''} ${manualDragOverIndex === index && manualIsDragging ? 'drag-target' : ''}`}
              data-index={index}
            >
              <div
                className={`post-edit-image-container ${manualDraggedIndex === index && manualIsDragging ? 'being-dragged' : ''}`}
                onMouseDown={(e) => handleManualMouseDown(e, index)}
                onTouchStart={(e) => handleManualTouchStart(e, index)}
                style={manualIsDragging && manualDraggedIndex === index ? { opacity: 0 } : undefined}
              >
                {renderMediaContent()}
                <div className="post-edit-image-number">
                  {index + 1}
                </div>
              </div>
            </div>
          );
        })}
        <div
          className={`post-edit-add-image-item ${manualIsDragging ? 'delete-zone' : ''} ${manualIsDragging && manualIsOverTrash ? 'active' : ''}`}
          data-delete-zone="true"
        >
          {!manualIsDragging ? (
            <div className="post-edit-add-image-content">
              <div className="post-edit-add-image-buttons">
                <button
                  type="button"
                  className="post-edit-add-image-main-btn"
                  onClick={() => manualFileInputRef.current?.click()}
                  aria-label={t('postReview.postEdit.media.uploadAria', { defaultValue: 'Carica da dispositivo' })}
                >
                  <span className="post-edit-add-image-btn-icon">
                    <UploadIcon size={18} />
                  </span>
                  <span>{t('postReview.postEdit.media.uploadLabel', { defaultValue: 'Carica' })}</span>
                </button>

                <button
                  type="button"
                  className="post-edit-add-image-main-btn"
                  onClick={handleManualPromptAdd}
                  disabled={manualImageGenerating}
                  aria-label={t('postReview.postEdit.media.generateAria', { defaultValue: 'Genera con AI' })}
                >
                  <span className="post-edit-add-image-btn-icon">
                    <img src={iconAi} alt="AI" />
                  </span>
                  <span>{t('postReview.postEdit.media.generateLabel', { defaultValue: 'Genera' })}</span>
                </button>

                <button
                  type="button"
                  className="post-edit-add-image-main-btn"
                  onClick={() => openGallery('manual')}
                  aria-label={t('postReview.postEdit.media.galleryAria', { defaultValue: 'Aggiungi da galleria' })}
                >
                  <span className="post-edit-add-image-btn-icon">
                    <GalleryIcon size={18} />
                  </span>
                  <span>{t('postReview.postEdit.media.galleryLabel', { defaultValue: 'Galleria' })}</span>
                </button>
              </div>
              <input
                ref={manualFileInputRef}
                type="file"
                accept={SUPPORTED_MEDIA_ACCEPT}
                multiple
                hidden
                onChange={(e) => {
                  handleManualFiles(e.target.files);
                  e.target.value = '';
                }}
              />
            </div>
          ) : (
            <div className="post-edit-delete-zone" data-delete-zone="true">
              <div className="post-edit-delete-icon">
                <TrashIcon size={32} />
              </div>
              <span className="post-edit-delete-text">
                {manualIsOverTrash
                  ? t('postReview.postEdit.media.deleteDrop', { defaultValue: 'Rilascia per eliminare' })
                  : t('postReview.postEdit.media.deleteDrag', { defaultValue: 'Trascina qui per eliminare' })}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderPostAuto = () => (
    <div className="prompt-editor-pro-mode post-prompt-section">
      <div className="prompt-editor-section">
        <h3 className="prompt-editor-section-title">{t('imageGenerator.post.auto.promptTitle', { defaultValue: 'Comando per generare il post' })}</h3>
        <textarea
          className="prompt-editor-simple-textarea"
          value={postPrompt}
          onChange={(e) => setPostPrompt(e.target.value)}
          placeholder={t('imageGenerator.post.auto.promptPlaceholder', { defaultValue: 'Scrivi cosa vuoi generare per il post...' })}
          rows={5}
        />
      </div>

      <div className="prompt-editor-footer">
        <button
          className="prompt-editor-generate-image"
          onClick={handleGeneratePostAuto}
          disabled={autoPostLoading || isSchedulingBusy}
        >
          {autoPostLoading ? (
            <>
              <div className="prompt-editor-spinner" />
              {t('imageGenerator.post.auto.loading', { defaultValue: 'Generazione post...' })}
            </>
          ) : (
            <>
              <img src={iconImage} alt={t('imageGenerator.reference.alt.image', { defaultValue: 'Immagine' })} className="button-icon" />
              {t('imageGenerator.post.auto.cta', { defaultValue: 'Genera post' })}
            </>
          )}
        </button>
      </div>
    </div>
  );

  const renderPostManual = () => (
    <div className="prompt-editor-pro-mode post-manual-section">
      <div className="generation-settings-card">
        {renderManualMediaGrid()}

        <div className="post-edit-section">
          <div className="post-edit-section-header">
            <div className="post-edit-section-title-group">
              <h2 className="post-edit-section-title">{t('postReview.postEdit.description.title', { defaultValue: 'Descrizione' })}</h2>
              <button
                className="btn-base btn-sm btn-secondary"
                onClick={handleOpenDescriptionModal}
                disabled={descriptionLoading || manualLoading}
                aria-label={t('postReview.postEdit.description.aiAria', { defaultValue: 'Genera descrizione con AI' })}
              >
                {descriptionLoading ? (
                  <div className="post-edit-loading-spinner" />
                ) : (
                  <img
                    src={iconAi}
                    alt={t('postReview.postEdit.description.aiAria', { defaultValue: 'Genera descrizione con AI' })}
                  />
                )}
              </button>
            </div>
          </div>
          <p className="post-edit-section-description">
            {t('postReview.postEdit.description.subtitle', { defaultValue: 'Puoi scrivere o generare con AI.' })}
          </p>

          <div className="post-edit-text-container">
            {descriptionLoading ? (
              <div className="post-edit-textarea-wrapper ai-generating">
                <AILoadingAnimation text={t('postReview.postEdit.description.loading', { defaultValue: 'Generazione...' })} />
              </div>
            ) : (
              <textarea
                className="post-edit-textarea"
                value={manualDescription}
                onChange={(e) => setManualDescription(e.target.value)}
                placeholder={t('postReview.postEdit.description.placeholder', { defaultValue: 'Scrivi qui la descrizione del post...' })}
                rows={8}
                disabled={manualLoading}
              />
            )}

            <div className="post-edit-text-save-section">
              <button
              className="btn-base btn-lg btn-gradient post-edit-text-save-btn"
              onClick={handleCreatePostManual}
              disabled={manualLoading || isSchedulingBusy}
            >
                {manualLoading ? (
                  <div className="post-edit-loading-spinner" />
                ) : (
                  <SaveIcon size={18} />
                )}
                <span>
                  {manualLoading
                    ? t('imageGenerator.post.manual.loading', { defaultValue: 'Creazione...' })
                    : t('imageGenerator.post.manual.cta', { defaultValue: 'Crea post' })}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>
  );

  const renderPostTab = () => (
    <div className="prompt-editor-pro-mode">
      {renderPostContextSelectors(postMode)}
      <div className="prompt-editor-mode-toggle-container">
        <div className="prompt-editor-mode-toggle">
          <button
            className={`prompt-editor-mode-toggle-option ${postMode === 'auto' ? 'active' : ''}`}
            onClick={() => setPostMode('auto')}
          >
            {t('imageGenerator.post.auto.label', { defaultValue: 'Automatica' })}
          </button>
          <button
            className={`prompt-editor-mode-toggle-option ${postMode === 'manual' ? 'active' : ''}`}
            onClick={() => setPostMode('manual')}
          >
            {t('imageGenerator.post.manual.label', { defaultValue: 'Manuale' })}
          </button>
        </div>
      </div>
      {postMode === 'auto' ? renderPostAuto() : renderPostManual()}
    </div>
  );

  const renderLoadingSkeleton = () => (
    <div className="mobile-ai-storage-content mobile-ai-prompt-editor-skeleton" aria-hidden="true">
      <div className="generation-page-header">
        <div className="mobile-ai-storage-header">
          <Skeleton width="96px" height="22px" shimmer />
          <Skeleton width="90%" height="14px" shimmer />
        </div>
      </div>

      <div className="prompt-editor-mode-toggle-container">
        <div className="prompt-editor-mode-toggle skeleton-toggle">
          <Skeleton variant="rounded" width="30%" height="38px" shimmer />
          <Skeleton variant="rounded" width="30%" height="38px" shimmer />
          <Skeleton variant="rounded" width="30%" height="38px" shimmer />
        </div>
      </div>

      <div className="generation-settings-card prompt-editor-skeleton-card">
        <div className="generation-settings-header">
          <Skeleton width="60%" height="16px" shimmer />
          <Skeleton width="80%" height="12px" shimmer />
        </div>
        <div className="prompt-editor-skeleton-grid">
          <Skeleton variant="rounded" width="100%" height="44px" shimmer />
          <div className="prompt-editor-skeleton-toggle-row">
            <Skeleton width="45%" height="12px" shimmer />
            <Skeleton variant="rounded" width="72px" height="28px" shimmer />
          </div>
        </div>
      </div>

      <div className="generation-settings-card prompt-editor-skeleton-card">
        <div className="generation-settings-header">
          <Skeleton width="55%" height="16px" shimmer />
          <Skeleton width="70%" height="12px" shimmer />
        </div>
        <div className="prompt-editor-skeleton-grid">
          <Skeleton variant="rounded" width="100%" height="52px" shimmer />
          <div className="prompt-editor-skeleton-toggle-row">
            <Skeleton width="48%" height="12px" shimmer />
            <Skeleton variant="rounded" width="72px" height="28px" shimmer />
          </div>
        </div>
      </div>

      <div className="generation-settings-card prompt-editor-skeleton-card">
        <div className="generation-settings-header">
          <Skeleton width="60%" height="16px" shimmer />
          <Skeleton width="70%" height="12px" shimmer />
        </div>
        <div className="prompt-editor-skeleton-grid two-columns">
          <Skeleton variant="rounded" width="100%" height="46px" shimmer />
          <Skeleton variant="rounded" width="100%" height="46px" shimmer />
        </div>
      </div>

      <div className="prompt-editor-pro-mode prompt-editor-skeleton-card">
        <div className="prompt-editor-mode-toggle-container">
          <div className="prompt-editor-mode-toggle skeleton-toggle">
            <Skeleton variant="rounded" width="48%" height="34px" shimmer />
            <Skeleton variant="rounded" width="48%" height="34px" shimmer />
          </div>
        </div>
        <div className="prompt-editor-section">
          <Skeleton width="50%" height="16px" shimmer />
          <Skeleton variant="rounded" width="100%" height="120px" shimmer />
        </div>
        <div className="prompt-editor-skeleton-footer">
          <Skeleton variant="rounded" width="48%" height="44px" shimmer />
          <Skeleton variant="rounded" width="48%" height="44px" shimmer />
        </div>
      </div>
    </div>
  );

  const showSkeleton = !hasInitialDataLoaded && (isLoading || campaignsLoading || companyLoading);
  const datePickerOverlay = isDatePickerOpen && typeof document !== 'undefined'
    ? createPortal(
        (
          <div
            className="mobile-date-picker-overlay"
            onClick={() => setIsDatePickerOpen(false)}
          >
            <div
              className="mobile-date-picker-popover"
              id="mobile-date-picker-popover"
              ref={datePickerRef}
              role="dialog"
              aria-modal="true"
              aria-label={t('postReview.postEdit.time.label')}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mobile-date-picker comp-503">
                <div className="mobile-date-picker-header">
                  <button
                    type="button"
                    className="date-picker-nav"
                    onClick={goToPreviousMonth}
                    aria-label={t('postReview.postEdit.time.prevMonth', { defaultValue: 'Mese precedente' })}
                  >
                    <BackIcon size={18} />
                  </button>
                  <div className="mobile-date-picker-month">{monthLabel}</div>
                  <button
                    type="button"
                    className="date-picker-nav next"
                    onClick={goToNextMonth}
                    aria-label={t('postReview.postEdit.time.nextMonth', { defaultValue: 'Mese successivo' })}
                  >
                    <BackIcon size={18} />
                  </button>
                </div>

                <div className="mobile-date-picker-body single-column">
                  <div className="mobile-date-picker-calendar">
                    <div className="mobile-date-picker-weekdays">
                      {weekdayLabels.map((label) => (
                        <span key={label} className="mobile-date-picker-weekday">
                          {label}
                        </span>
                      ))}
                    </div>
                    <div className="mobile-date-picker-grid">
                      {calendarDays.map((day, index) => {
                        if (!day) {
                          return <span key={`empty-${index}`} className="mobile-date-picker-day empty"></span>;
                        }
                        const isSelected = isSameDay(day, selectedDate);
                        const isToday = isSameDay(day, new Date());
                        const isDisabled = day < today;
                        return (
                          <button
                            type="button"
                            key={day.getTime()}
                            className={`mobile-date-picker-day ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${isDisabled ? 'disabled' : ''}`}
                            onClick={() => handleDateSelect(day)}
                            disabled={isSchedulingBusy || isDisabled}
                          >
                            <span>{day.getDate()}</span>
                            {isToday && <span className="mobile-date-picker-dot"></span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mobile-date-picker-time-input">
                    <div className="time-input-label-row">
                      <ClockIcon size={16} />
                      <span>{t('postReview.postEdit.time.chooseHour', { defaultValue: 'Scegli orario' })}</span>
                    </div>
                    <div className="time-input-wrapper" onClick={openTimePicker} role="button" tabIndex={0}>
                      <input
                        type="time"
                        step="60"
                        value={selectedTime || '12:00'}
                        onChange={(e) => handleTimeInputChange(e.target.value)}
                        className="time-input-field"
                        disabled={isSchedulingBusy}
                        ref={timeInputRef}
                      />
                      <div className="time-input-icon">
                        <ClockIcon size={16} aria-hidden="true" />
                      </div>

                      {isTimePickerOpen && (
                        <div className="time-picker-panel" ref={timePanelRef}>
                          <div className="time-picker-columns">
                            <div className="time-picker-column">
                              <div className="time-picker-column-label">{t('postReview.postEdit.time.hours', { defaultValue: 'Ore' })}</div>
                              <div className="time-picker-scroll">
                                {hourOptions.map((hour) => {
                                  const isSelectedHour = (selectedTime || '12:00').startsWith(hour);
                                  return (
                                    <button
                                      type="button"
                                      key={hour}
                                      className={`time-picker-option ${isSelectedHour ? 'selected' : ''}`}
                                      onClick={() => handleHourSelect(hour)}
                                    >
                                      {hour}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                            <div className="time-picker-column">
                              <div className="time-picker-column-label">{t('postReview.postEdit.time.minutes', { defaultValue: 'Minuti' })}</div>
                              <div className="time-picker-scroll">
                                {minuteOptions.map((minute) => {
                                  const isSelectedMinute = (selectedTime || '12:00').split(':')[1] === minute;
                                  return (
                                    <button
                                      type="button"
                                      key={minute}
                                      className={`time-picker-option ${isSelectedMinute ? 'selected' : ''}`}
                                      onClick={() => handleMinuteSelect(minute)}
                                    >
                                      {minute}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ),
        document.body
      )
    : null;

  if (
    showGallery === 'reference'
    || showGallery === 'manual'
    || showGallery === 'video-first-frame'
    || showGallery === 'video-last-frame'
  ) {
    return (
      <ImageGalleryPage
        onBack={closeGallery}
        onImageSelect={(image) => {
          if (showGallery === 'reference') {
            handleReferenceSelect(image);
          } else if (showGallery === 'manual') {
            handleManualGallerySelect(image);
          } else {
            handleVideoFrameSelect(image, showGallery === 'video-first-frame' ? 'first' : 'last');
          }
        }}
      />
    );
  }

  if (showSkeleton) {
    return (
      <div className="mobile-ai-storage mobile-ai-prompt-editor">
        {renderLoadingSkeleton()}
      </div>
    );
  }

  return (
    <>
      <div className="mobile-ai-storage mobile-ai-prompt-editor">
        <div className="mobile-ai-storage-content">
          <div className="generation-page-header">
            <div className="mobile-ai-storage-header">
              <h1 className="mobile-ai-storage-title">{t('imageGenerator.header.title', { defaultValue: 'Crea' })}</h1>
              <p className="mobile-ai-storage-subtitle">
              {t('imageGenerator.header.subtitle', { defaultValue: 'Utilizza Loopino per dare una spinta alla tua creativita nella creazione di post o immagini' })}
            </p>
            {bootstrapError && (
              <div className="prompt-editor-validation">
                {bootstrapError}
              </div>
            )}
          </div>
        </div>

        <div className="prompt-editor-mode-toggle-container">
          <TripleOptionToggle
            value={activeTab}
            onChange={setActiveTab}
            labels={[
              { value: 'post', label: t('imageGenerator.tabs.post', { defaultValue: 'Post' }) },
              { value: 'image', label: t('imageGenerator.tabs.image', { defaultValue: 'Immagine' }) },
              { value: 'video', label: t('imageGenerator.tabs.video', { defaultValue: 'Video' }) }
            ]}
            ariaLabel={t('imageGenerator.tabs.toggleAria', { defaultValue: 'Seleziona tra post, immagini e video' })}
            className="prompt-editor-main-toggle"
          />
        </div>

        {activeTab === 'image'
          ? renderImageTab()
          : activeTab === 'video'
            ? renderVideoTab()
            : renderPostTab()}
      </div>

      <AIRegenerationModal
        isOpen={showPromptBlocksModal}
        onClose={handleClosePromptBlocksModal}
        type="blocks"
        onRegenerate={handlePromptBlocksRegeneration}
        isRegenerating={isRegeneratingBlocks}
      />

      {manualAIModalOpen && (
        <AIRegenerationModal
          isOpen={manualAIModalOpen}
          onClose={closeManualAIModal}
          type={manualAiModalType || 'text'}
          onRegenerate={handleManualAIModalRegenerate}
          isRegenerating={manualAiModalType === 'image' ? manualImageGenerating : descriptionLoading}
          extraContent={
            manualAiModalType === 'text' ? (
              <div className="generation-settings-toggle ai-modal-context-toggles">
                <div className="generation-toggle-row">
                  <div className="generation-toggle-title">
                    {t('imageGenerator.post.auto.useDnaTitle', { defaultValue: 'Usa DNA per la generazione' })}
                  </div>
                  <DualOptionToggle
                    active={useDnaForPost}
                    onChange={(next) => setUseDnaForPost(next)}
                    leftLabel={t('imageGenerator.toggle.off', { defaultValue: 'Off' })}
                    rightLabel={t('imageGenerator.toggle.on', { defaultValue: 'On' })}
                    ariaLabel={t('imageGenerator.post.auto.dnaToggleAria', { defaultValue: 'Attiva o disattiva l\'uso del DNA per il post' })}
                  />
                </div>
                <div className="generation-toggle-row">
                  <div className="generation-toggle-title">
                    {t('imageGenerator.post.auto.useStrategyTitle', { defaultValue: 'Usa strategia per la generazione' })}
                  </div>
                  <DualOptionToggle
                    active={useStrategyForPost}
                    onChange={(next) => setUseStrategyForPost(next)}
                    leftLabel={t('imageGenerator.toggle.off', { defaultValue: 'Off' })}
                    rightLabel={t('imageGenerator.toggle.on', { defaultValue: 'On' })}
                    ariaLabel={t('imageGenerator.post.auto.strategyToggleAria', { defaultValue: 'Attiva o disattiva la strategia di campagna' })}
                  />
                </div>
              </div>
            ) : null
          }
        />
      )}

      {/* Drag Ghost for manual media */}
      {manualIsDragging && manualDraggedIndex !== null && manualMedia[manualDraggedIndex] && (
        <div
          className="post-edit-drag-ghost"
          style={{
            position: 'fixed',
            left: manualDragCurrentPos.x - (manualDraggedImageDimensions.width / 2),
            top: manualDragCurrentPos.y - (manualDraggedImageDimensions.height / 2),
            width: `${manualDraggedImageDimensions.width}px`,
            height: `${manualDraggedImageDimensions.height}px`,
            pointerEvents: 'none',
            zIndex: 1000,
            opacity: 0.85,
            transform: 'rotate(2deg) scale(1.02)',
            borderRadius: '12px',
            overflow: 'hidden',
            border: '2px solid rgba(255, 255, 255, 0.8)',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.45)',
            transition: 'none'
          }}
        >
          {(() => {
            const mediaItem = manualMedia[manualDraggedIndex];
            const displaySource = mediaItem?.previewUrl || mediaItem?.url || placeholderImage;
            const thumbnailKey = `${mediaItem?.id || displaySource}-${manualDraggedIndex}`;
            const isVideo = mediaItem?.file
              ? !!mediaItem.file && mediaItem.file.type?.startsWith('video/')
              : isVideoFile(mediaItem?.url || mediaItem?.previewUrl);
            const thumbnail = manualVideoThumbnails[thumbnailKey];
            const ghostSrc = isVideo && thumbnail ? thumbnail : displaySource;

            return (
              <>
                <img
                  src={ghostSrc}
                  alt={`Dragging ${isVideo ? 'video' : 'image'} ${manualDraggedIndex + 1}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => { e.target.src = placeholderImage; }}
                />
                {isVideo && thumbnail && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      width: '24px',
                      height: '24px',
                      background: 'rgba(0, 0, 0, 0.7)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <polygon points="5,3 19,12 5,21" fill="white"/>
                    </svg>
                  </div>
                )}
              </>
            );
          })()}

          <div
            style={{
              position: 'absolute',
              bottom: '8px',
              left: '8px',
              width: '28px',
              height: '28px',
              background: 'rgba(255, 255, 255, 0.92)',
              color: '#111111',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              fontWeight: '700',
              border: '1px solid rgba(0, 0, 0, 0.08)',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.25)'
            }}
          >
            {manualDraggedIndex + 1}
          </div>
        </div>
      )}
      </div>
      {datePickerOverlay}
    </>
  );
};

export default ImageGenerationPage;
